

// © 2001-2015 INOVx Solutions, Inc.
// 114 Pacifica Suite 300, Irvine, CA 92618 USA
// All rights reserved.
//
// This software is confidential, proprietary and unpublished property of
// INOVx Solutions, Inc.  It may NOT be copied in part or in whole on any
// medium, either electronic or printed, without the express written consent
// of INOVx Solutions, Inc.

import * as BABYLON from 'babylonjs';
import * as BABYLONGUI from 'babylonjs-gui';
import * as BABYLONEXPORT from 'babylonjs-serializers';
import Draco3d from 'draco3d';
import Hammer from 'hammerjs';
import * as Interfaces from '../../Interfaces/Interfaces';
import ExamineCamera from '../Cameras/ExamineCamera';
import FollowCamera from '../Cameras/FollowCamera';
import UniversalCamera from '../Cameras/UniversalCamera';
import ArcCamera from '../Cameras/ArcCamera';
import * as Enumerations from '../../Enumerations/Enumerations';
import * as Data from '../Data/WebData';
import Textures from '../lodviewer/Resources/SceneTextures';
import * as FrustumMeshCoordinator from '../workers/frustumMeshCoordinator';
import * as FrustumMeshWorker from '../workers/frustumMeshWorker';
import * as FrustumRegionCoordinator from '../workers/frustumRegionCoordinator';
import * as FrustumRegionWorker from '../workers/frustumRegionWorker';
import * as FrustumStructures from '../workers/frustumStructures';
import DebugOptions from '../lodviewer/DebugOptions';
import { number } from 'prop-types';
import { Scene } from 'babylonjs/Legacy/legacy';
//import { DecoderModule } from 'draco3d';
//const draco = require('draco3d');
import { PickObjectType } from '../../Enumerations/Enumerations';
import * as Inovx from '@shared';
var UrlUtilities = Inovx.Shared.Common.UrlUtilities;

type CameraPose = {
    position: BABYLON.Vector3;
    target: BABYLON.Vector3;
    quaternion: BABYLON.Quaternion;      
    mode: number;                       // BABYLON.Camera.PERSPECTIVE_CAMERA / ORTHOGRAPHIC_CAMERA
    focalDistance: number;              
    cameraHeight?: number;              
};


type CameraMode = Enumerations.CameraMode;

type AnyCam = BABYLON.Camera & {
    mode: number; // BABYLON.Camera.PERSPECTIVE_CAMERA / ORTHOGRAPHIC_CAMERA
    minZ: number;
    maxZ: number;

    setTarget?: (v: BABYLON.Vector3) => void;
    getTarget?: () => BABYLON.Vector3;
    target?: BABYLON.Vector3;
    lockedTarget?: BABYLON.Vector3;
    _sceneExtentZoomFactor: number;
};

function isExamineCamera(cam: AnyCam): cam is ExamineCamera {
    return cam instanceof ExamineCamera;
}

function isUniversalCamera(cam: AnyCam): cam is UniversalCamera {
    return cam instanceof UniversalCamera;
}


type CamLike = BABYLON.ArcRotateCamera | BABYLON.UniversalCamera | BABYLON.FreeCamera | BABYLON.TargetCamera;
type EaseKind =
    | "linear"
    | "quadInOut"
    | "cubicInOut"
    | "quintOut"     
    | "expoOut";

type CameraAnimKind = "initial" | "normal";

interface IQueuedCameraRequest {
    scene: BABYLON.Scene;
    cam: CamLike;
    opts: {
        position?: BABYLON.Vector3;
        target?: BABYLON.Vector3;
        maxMs?: number;
        easing?: EaseKind;
        onUpdate?: (pos: BABYLON.Vector3, tgt: BABYLON.Vector3) => void;
        targetLead?: number;
        preserveForward?: boolean | "auto";
        lockForward?: { dir: BABYLON.Vector3; dist0: number; dist1: number };
        kind?: CameraAnimKind;
        queueIfBlocked?: boolean;
    };
    resolve: () => void;
    reject: (err?: any) => void;
}


function setupScreenSpaceCurvature(scene: BABYLON.Scene, camera: BABYLON.Camera, ridge: number, valley: number): BABYLON.PostProcess {
    const geometryBufferRenderer = scene.enableGeometryBufferRenderer();
    geometryBufferRenderer.renderList = [];
    const normalTexture = geometryBufferRenderer.getGBuffer().textures[1];
    const postProcess = new BABYLON.PostProcess("Screen Space Curvature", "ssc",
        ["curvature_ridge", "curvature_valley"], ["normalSampler"], 1 /* ratio */, camera);
    (postProcess as any).ridge = ridge;
    (postProcess as any).valley = valley;
    postProcess.onApply = function (effect) {

        effect.setFloat("curvature_ridge", 0.5 / Math.max((postProcess as any).ridge * (postProcess as any).ridge, 1e-4));
        effect.setFloat("curvature_valley", 0.7 / Math.max((postProcess as any).valley * (postProcess as any).valley, 1e-4));
        effect.setTexture("normalSampler", normalTexture);
    };

    return postProcess;
}
function removeScreenSpaceCurvature(scene: BABYLON.Scene, camera: BABYLON.Camera) {

    camera.detachPostProcess(this._edgeProcess);
}

BABYLON.Effect.ShadersStore["sscFragmentShader"] = `
    // From Blender sources: https://developer.blender.org/D3617#change-jG1fqR1RpokL
    precision highp float;
    // Samplers
    varying vec2 vUV;
    uniform sampler2D textureSampler;
    uniform sampler2D normalSampler;
	uniform float curvature_ridge;
	uniform float curvature_valley;
    #ifndef CURVATURE_OFFSET
    #define CURVATURE_OFFSET 1
    #endif
    float curvature_soft_clamp(float curvature, float control)
    {
        if (curvature < 0.5 / control)
            return curvature * (1.0 - curvature * control);
        return 0.25 / control;
    }
    float calculate_curvature(ivec2 texel, float ridge, float valley)
    {
        /*uint object_up    = texelFetchOffset(objectId, texel, 0, ivec2(0,  CURVATURE_OFFSET)).r;
        uint object_down  = texelFetchOffset(objectId, texel, 0, ivec2(0, -CURVATURE_OFFSET)).r;
        uint object_left  = texelFetchOffset(objectId, texel, 0, ivec2(-CURVATURE_OFFSET, 0)).r;
        uint object_right = texelFetchOffset(objectId, texel, 0, ivec2( CURVATURE_OFFSET, 0)).r;
        if((object_up != object_down) || (object_right != object_left)) {
            return 0.0;
        }*/
        vec2 normal_up    = texelFetchOffset(normalSampler, texel, 0, ivec2(0,  CURVATURE_OFFSET)).rb;
        vec2 normal_down  = texelFetchOffset(normalSampler, texel, 0, ivec2(0, -CURVATURE_OFFSET)).rb;
        vec2 normal_left  = texelFetchOffset(normalSampler, texel, 0, ivec2(-CURVATURE_OFFSET, 0)).rb;
        vec2 normal_right = texelFetchOffset(normalSampler, texel, 0, ivec2( CURVATURE_OFFSET, 0)).rb;
        float normal_diff = ((normal_up.g - normal_down.g) + (normal_right.r - normal_left.r));
        if (normal_diff < 0.0)
            return -2.0 * curvature_soft_clamp(-normal_diff, valley);
        return 2.0 * curvature_soft_clamp(normal_diff, ridge);
    }
    void main(void) 
    {
        ivec2 texel = ivec2(gl_FragCoord.xy);
        vec4 baseColor = texture2D(textureSampler, vUV);
        float curvature = calculate_curvature(texel, curvature_ridge, curvature_valley);
        baseColor.rgb *= curvature + 1.0;
        gl_FragColor = baseColor;
    }
    `;

class SceneManager implements Interfaces.Interfaces.ISceneManager, Interfaces.Interfaces.IRayPickProvider {

    private _initialCameraAnimationInProgress = false;
    private _initialCameraAnimationCompleted = false;
    
    private _queuedCameraRequest: IQueuedCameraRequest | null = null;

    private _activeAnim = new WeakMap<CamLike, { obs: BABYLON.Observer<BABYLON.Scene>, token: number }>();
    private _cameraAnimationActive = false;
    private _cameraAnimationCoordinatorThrottleEveryNFrames = 3;
    private _cameraAnimationFrameCounter = 0;
    private _onKeyDown: (e: KeyboardEvent) => void;
    private _babScene: BABYLON.Scene;
    private _edgeProcess: BABYLON.PostProcess;
    private _shaderEnabled: boolean = false;
    private _edgeValley: number = .6;
    private _edgeRidge: number = .3;
    private _lastProcessedMeshesArrayVersion: number = 0;
    private _lastProcessedGlbMeshesArrayVersion: number = 0;
    private _sceneContentExtents: BABYLON.BoundingBox;
    private _markedPointsManager: Interfaces.Interfaces.IAnnotationManager;
    private _selectionManager: Interfaces.Interfaces.ISelectionManager;
    private _meshManager: Interfaces.Interfaces.IMeshManager;
    private _measurementManager: Interfaces.Interfaces.IMeasurementManager;
    private _labelManager: Interfaces.Interfaces.ILabelManager;
    private _assemblyManager: Interfaces.Interfaces.IAssemblyManager;
    private _pickMode: Enumerations.PickMode;
    private _selectMode: Enumerations.SelectMode;
    private _clickMode: Enumerations.ClickMode;
    private _displayManager: Interfaces.Interfaces.IDisplayManager;
    private _babEngine: BABYLON.Engine;
    private _annotationCanvas2dContext: CanvasRenderingContext2D;
    private _annotationCanvas2dElement: HTMLCanvasElement;
    private _drawCanvas2dContext: CanvasRenderingContext2D;
    private _drawCanvas2dElement: HTMLCanvasElement;
    private _eventHandler: Interfaces.Interfaces.IEventManager;
    private _dragging: boolean;
    private _draggingYStart: number;
    private _draggingXStart: number;
    private _skyBox: BABYLON.Mesh;
    private _skyBoxTexture: BABYLON.StandardMaterial;
    private _lastTapTime: number;
    private _allowPick: boolean
    private _storedCameraView: StoredCameraView;
    private _coordinator: FrustumMeshCoordinator.FrustumMeshCoordinatorWorker;
    private _regionCoordinators: FrustumRegionCoordinator.FrustumRegionCoordinatorWorker[] = [];
    private _dataRequestCallback: (displayManager: Interfaces.Interfaces.IDisplayManager, visibleNodes: BABYLON.SmartArray<FrustumStructures.TreeNode>, hiddenNodes: BABYLON.SmartArray<FrustumStructures.TreeNode>) => void;
    private _dataRegionRequestCallback: (displayManager: Interfaces.Interfaces.IDisplayManager, visibleNodes: BABYLON.SmartArray<FrustumStructures.TileNode>, hiddenNodes: BABYLON.SmartArray<FrustumStructures.TileNode>) => void;
    private _routes: Interfaces.Interfaces.IFollowCameraRoute[] = [];
    private _followCamera: FollowCamera;
    private _examineCamera: ExamineCamera;
    private _universalCamera: UniversalCamera;
    private _materialManager: Interfaces.Interfaces.IMaterialsManager;
    private _hemispericLights: BABYLON.HemisphericLight[] = null;
    private _ambientIntensity: number = .6;
    private _crosshair: BABYLONGUI.Rectangle;
    public enableThinInstanceMotionMode: boolean = false;
    private _thinInstanceMotionModeActive: boolean = false;
    get scene() {
        return this._babScene;
    }

    constructor(eventHander: Interfaces.Interfaces.IEventManager,
        displayManager: Interfaces.Interfaces.IDisplayManager, meshManager: Interfaces.Interfaces.IMeshManager,
        selectionManager: Interfaces.Interfaces.ISelectionManager, markedPointsManager: Interfaces.Interfaces.IAnnotationManager,
        materialManager: Interfaces.Interfaces.IMaterialsManager, measurementManager: Interfaces.Interfaces.IMeasurementManager,
        labelManager: Interfaces.Interfaces.ILabelManager, assemblyManager: Interfaces.Interfaces.IAssemblyManager,
        scene: BABYLON.Scene, annotationCanvas2d: HTMLCanvasElement, drawCanvas2d: HTMLCanvasElement, engine: BABYLON.Engine) {

        this._selectionManager = selectionManager;
        this._materialManager = materialManager;
        this._meshManager = meshManager;
        this._measurementManager = measurementManager;
        this._labelManager = labelManager;
        this._assemblyManager = assemblyManager;
        this._annotationCanvas2dContext = (annotationCanvas2d as HTMLCanvasElement).getContext("2d");
        this._annotationCanvas2dElement = annotationCanvas2d;
        this._drawCanvas2dContext = (drawCanvas2d as HTMLCanvasElement).getContext("2d");
        this._drawCanvas2dElement = drawCanvas2d;
        this._clickMode = Enumerations.ClickMode.NAVIGATE;
        this._pickMode = Enumerations.PickMode.SELECT;
        this._selectMode = Enumerations.SelectMode.SINGLE;
        this._displayManager = displayManager;
        this._markedPointsManager = markedPointsManager;
        this._babEngine = engine;
        this._eventHandler = eventHander;
        this._annotationCanvas2dContext.canvas.width = annotationCanvas2d.clientWidth / engine.getHardwareScalingLevel();
        this._annotationCanvas2dContext.canvas.height = annotationCanvas2d.clientHeight / engine.getHardwareScalingLevel();

        this._drawCanvas2dContext.canvas.width = drawCanvas2d.clientWidth / engine.getHardwareScalingLevel();
        this._drawCanvas2dContext.canvas.height = drawCanvas2d.clientHeight / engine.getHardwareScalingLevel();

        this._allowPick = true;
        this._dragging = false;

        this._babScene = scene;

        var that = this;

        var camera = new ExamineCamera("ExamineCamera", eventHander, 1, 0.8, 10, new BABYLON.Vector3(1, 0, 0), this._babScene);
        camera.position.x = -0;
        camera.position.y = -0;
        camera.mode = BABYLON.Camera.PERSPECTIVE_CAMERA;
        camera.inertia = 0;
        camera.speed = 10;
        camera._angularSensibilityX = window.innerWidth / Math.PI;
        camera._angularSensibilityY = window.innerHeight / Math.PI;

        this._examineCamera = camera;
        
        if (DebugOptions.valueOf()) {
            this._babScene.debugLayer.show();
        }

        // attach the light to the camera
        camera.attachControl(engine.getRenderingCanvas() as any, true, false);
        var canvas = document.createElement('canvas');
        var gl = canvas.getContext('webgl2') || canvas.getContext('experimental-webgl2');

        if (gl && gl instanceof WebGL2RenderingContext) {
            console.log('WebGL 2 is supported and used.');
        } else {
            gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
            if (gl && gl instanceof WebGLRenderingContext) {
                console.log('WebGL 1 is supported and used.');
            } else {
                console.log('WebGL is not supported.');
            }
        }

        var envTexture = BABYLON.CubeTexture.CreateFromPrefilteredData(
            UrlUtilities.getBaseApplicationUrl() + "/Assemblies/environmentSpecular.env",
            this._babScene
        );

        this._babScene.environmentTexture = envTexture;

        engine.runRenderLoop(function () {
            that.renderLoop();
            var cam = that.scene.activeCamera as any;
            var transformation = that._babScene.getTransformMatrix();
            var r1 = new BABYLON.Vector4((transformation as any)._m[0], (transformation as any)._m[1], (transformation as any)._m[2], (transformation as any)._m[3]);
            var r2 = new BABYLON.Vector4((transformation as any)._m[4], (transformation as any)._m[5], (transformation as any)._m[6], (transformation as any)._m[7]);
            var r3 = new BABYLON.Vector4((transformation as any)._m[8], (transformation as any)._m[9], (transformation as any)._m[10], (transformation as any)._m[11]);
            var r4 = new BABYLON.Vector4((transformation as any)._m[12], (transformation as any)._m[13], (transformation as any)._m[14], (transformation as any)._m[15]);
            var transMat = [r1.x, r1.y, r1.z, r1.w,
            r2.x, r2.y, r2.z, r2.w,
            r3.x, r3.y, r3.z, r3.w,
            r4.x, r4.y, r4.z, r4.w];


            
            var cameraDirection = cam.target.subtract(cam.position).normalize();

            const shouldUpdateCoordinators = !that._cameraAnimationActive;

            if (shouldUpdateCoordinators) {
                for (var x = 0; x < that._regionCoordinators.length; x++) {
                    that._regionCoordinators[x].getUpdatedNodeStatus(1, 5, cam.position, cameraDirection, BABYLON.Frustum.GetPlanes(that._babScene.getTransformMatrix()), transMat, cam.viewport.toGlobal(that._babEngine.getRenderWidth(), that._babEngine.getRenderHeight()), that._displayManager.lodDistances, that.onRegionNodesUpdated);
                }
                if (that._dataRequestCallback != null) {

                    that._coordinator.getUpdatedNodeStatus(1, 5, cam.position, cam.target, BABYLON.Frustum.GetPlanes(that._babScene.getTransformMatrix()), transMat, cam.viewport.toGlobal(that._babEngine.getRenderWidth(), that._babEngine.getRenderHeight()), that.onNodesUpdated);
                }
            }
        });

        camera.orthoTop = window.innerHeight / 100;
        camera.orthoBottom = window.innerHeight / 100 * -1;
        camera.orthoLeft = window.innerWidth / 100 * -1;
        camera.orthoRight = window.innerWidth / 100;

        var hammerClicks = new Hammer(engine.getRenderingCanvas() as any);

        hammerClicks.get('tap').set({
            posThreshold: 0,
            interval: 0
        });
        var mc = new Hammer.Manager(drawCanvas2d, {
            recognizers: [
                // Define the Pan recognizer with specific options
                [Hammer.Pan, { direction: Hammer.DIRECTION_ALL, pointers: 1 }]
            ]
        });

        mc.get('pan').set({ direction: Hammer.DIRECTION_ALL, pointers: 0, threshold: .1 });

        window.addEventListener("orientationchange", function () {

            var camera = <ExamineCamera>that.scene.activeCamera;

            if (isExamineCamera(camera)) {
                camera.heightScale = that._babEngine.getRenderHeight() / that._babEngine.getRenderWidth();
                camera.widthScale = that._babEngine.getRenderWidth() / that._babEngine.getRenderHeight();
                that.setCameraMode(camera.mode);
            }
        });

        const onContextMenu = (ev: MouseEvent) => {

            // This is for right click, which for now only can select annotations.
            ev.preventDefault();
            ev.stopPropagation();

            if (!that._allowPick || (that.scene.activeCamera as any).isMoving()) return;
            if (that._assemblyManager.isInCreationMode()) return;

            const canvasRect = that._babEngine.getRenderingCanvasClientRect();
            const pointerX = ev.clientX - canvasRect.left;
            const pointerY = ev.clientY - canvasRect.top;

            if (that._pickMode === Enumerations.PickMode.SELECT) {
                const pickedAnnotationID = that._labelManager.pickAnnotationByClick(pointerX, pointerY);
                if (pickedAnnotationID) {

                    that._selectionManager.selectSingleObject(
                        pickedAnnotationID,
                        true,
                        true,
                        PickObjectType.Annotation,
                        ev 
                    );
                    return;
                }

                var cam = that.scene.activeCamera as any;
                var cameraViewport = cam.viewport;
                var viewport = cameraViewport.toGlobal(that._babEngine.getRenderWidth(), that._babEngine.getRenderHeight());
                that._meshManager.setPickableMeshes(true);

                try {
                    var pickResult = that.pickWithBackFaceExclusion(pointerX, pointerY, viewport, cam);

                    var pickedAssembly = null;
                    if (pickResult.info.pickedMesh != null) {
                        const parsedAssembly = that._tryParseAssemblyMeshId(pickResult.info.pickedMesh.id);

                        if (parsedAssembly) {
                            const [parsedAssemblyID, _, parsedInstanceID] = parsedAssembly;

                            if (that._assemblyManager.hasAssembly(parsedAssemblyID, parsedInstanceID)) {
                                pickedAssembly = pickResult.info.pickedMesh.id;
                                that._selectionManager.selectSingleObject(
                                    parsedInstanceID,
                                    true,
                                    true,
                                    PickObjectType.Assembly,
                                    ev
                                );
                                return;
                            }
                        }
                    }

                    if (pickResult.info.hit) {
                        if (pickResult.type === Enumerations.PickObjectType.ScanPos) {
                            // No action on scan positions
                        }
                        else {
                            var selectedObjectId = that._getSelectedObjectIdFromPick(pickResult);

                            var isAttachmentSphere = that._labelManager.isMeshAttachmentSphere(pickResult.info.pickedMesh);
                            // only select valid ids.
                            if (!isAttachmentSphere)
                            {
                                if (that._selectMode == Enumerations.SelectMode.SINGLE) {
                                    var isSelected = false;
                                    try {
                                        if (pickResult.info.pickedMesh != null) {
                                            if (pickResult.info.pickedMesh.id.indexOf(':') < 0) {
                                                // test if the selection is the already selected assembly.
                                                if (that._assemblyManager.isSelectedAssembly(pickResult.info.pickedMesh.id)) {
                                                    pickedAssembly = that._assemblyManager.getSelectedAssemblyID();
                                                }
                                            }
                                            else {
                                                const parsedAssembly = that._tryParseAssemblyMeshId(pickResult.info.pickedMesh.id);

                                                if (parsedAssembly) {
                                                    const [parsedAssemblyID, _, parsedInstanceID] = parsedAssembly;

                                                    if (that._assemblyManager.hasAssembly(parsedAssemblyID, parsedInstanceID)) {
                                                        pickedAssembly = pickResult.info.pickedMesh.id;
                                                    }
                                                }
                                            }
                                        }

                                        if (!pickedAssembly) {
                                            isSelected = that._selectionManager.selectSingleObject(selectedObjectId, true, true, pickResult.type, ev);
                                            // Currently context menu cannot pick marked points. Are we even using marked point manager!?
                                            //that._markedPointsManager.pickMarkedPoint(selectedObjectId, isSelected, true, false);
                                        }
                                    } catch (exc) {

                                    }

                                } else if (that._selectMode == Enumerations.SelectMode.ALL) {
                                    var selectedObjectId2 = that._meshManager.changeAllMaterialToNewMaterial(pickResult.info.pickedMesh.id, "DefaultMaterial");
                                }
                            }
                        }
                    }
                }
                finally {
                    that._meshManager.setPickableMeshes(false);
                }
            }
        };
        const canvasEl = engine.getRenderingCanvas() as HTMLCanvasElement;

        canvasEl.addEventListener("contextmenu", onContextMenu);

        hammerClicks.on('tap', function (ev: any) {
            if (that._lastTapTime == ev.srcEvent.timeStamp) {
                return;
            }
            else {
                that._lastTapTime = ev.srcEvent.timeStamp;
            }

            if (!that._allowPick || (that.scene.activeCamera as any).isMoving()) {
                return;
            }

            if (that._assemblyManager.isInCreationMode()) {
                return;
            }

            if (that._pickMode == Enumerations.PickMode.MEASUREMENT) {
                that._measurementManager.handleTap(ev.pointers[0].clientX, ev.pointers[0].clientY);
                return;
            }

            that._meshManager.setPickableMeshes(true);

            try {
                var canvasRect = that._babEngine.getRenderingCanvasClientRect();

                var pointerX = ev.pointers[0].clientX - canvasRect.left;
                var pointerY = ev.pointers[0].clientY - canvasRect.top;

                if (that._clickMode == Enumerations.ClickMode.REGION) {
                    that._drawCanvas2dContext.clearRect(0, 0, that._drawCanvas2dContext.canvas.width, that._drawCanvas2dContext.canvas.height);
                }

                // Check to see if a MP label is picked
                if (that._pickMode == Enumerations.PickMode.SELECT) {

                    const pickedAnnotationID = that._labelManager.pickAnnotationByClick(pointerX, pointerY);
                    if (pickedAnnotationID) {
                        that._selectionManager.selectSingleObject(pickedAnnotationID, true, true, PickObjectType.Annotation, ev.srcEvent);
                        return;
                    }

                    // var hardwareScalingLevel = 1;
                    // var pickedMpId = that._markedPointsManager.pickMarkedPointByClick(pointerX / hardwareScalingLevel, pointerY / hardwareScalingLevel);
                    // if (pickedMpId != "") {

                    //     that._selectionManager.unselectAllAssets(false);

                    //     if (that._markedPointsManager.isMarkedPointSelected(pickedMpId)) {
                    //         var isSelected = false;
                    //         isSelected = that._selectionManager.selectSingleObject(pickedMpId, true, true, Enumerations.PickObjectType.Object, ev.srcEvent);
                    //     } else {
                    //         that._selectionManager.selectionChanged([pickedMpId], false);
                    //     }

                    //     return;
                    // }
                }

                var cam = that.scene.activeCamera as any;
                var cameraViewport = cam.viewport;
                var viewport = cameraViewport.toGlobal(that._babEngine.getRenderWidth(), that._babEngine.getRenderHeight());
                var pickResult = that.pickWithBackFaceExclusion(pointerX, pointerY, viewport, cam);
                //var pickResult = that._babScene.pick(pointerX, pointerY);

                var pickPointCloudResult = that._pickPointCloud(pointerX, pointerY);

                if (pickResult.info.hit || pickPointCloudResult != null) {

                    var point = pickPointCloudResult != null && pickResult.info.hit
                        ? cam.position.subtract(pickResult.info.pickedPoint).length() < cam.position.subtract(pickPointCloudResult).length()
                            ? pickResult.info.pickedPoint
                            : pickPointCloudResult
                        : pickPointCloudResult != null
                            ? pickPointCloudResult
                            : pickResult.info.pickedPoint;

                    if (that._pickMode == Enumerations.PickMode.SELECT) {

                        if (pickResult.info.hit) {
                            if (pickResult.type === Enumerations.PickObjectType.ScanPos) {
                                // No action on scan positions
                            }
                            else {
                                var selectedObjectId = that._getSelectedObjectIdFromPick(pickResult);

                                var isAttachmentSphere = that._labelManager.isMeshAttachmentSphere(pickResult.info.pickedMesh);

                                if (!isAttachmentSphere) {
                                    if (that._selectMode == Enumerations.SelectMode.SINGLE) {
                                        var isSelected = false;

                                        try {
                                            let pickedAssembly = null;

                                            if (pickResult.info.pickedMesh != null) {
                                                const meshId = pickResult.info.pickedMesh.id;
                                                const parsedAssembly = that._tryParseAssemblyMeshId(meshId);

                                                if (parsedAssembly) {
                                                    const [parsedAssemblyID, _, parsedInstanceID] = parsedAssembly;

                                                    if (that._assemblyManager.hasAssembly(parsedAssemblyID, parsedInstanceID)) {
                                                        pickedAssembly = meshId;

                                                        that._selectionManager.selectSingleObject(
                                                            parsedInstanceID,
                                                            true,
                                                            true,
                                                            PickObjectType.Assembly,
                                                            ev.srcEvent
                                                        );
                                                        return;
                                                    }
                                                }
                                                else {
                                                    // test if the selection is the already selected assembly.
                                                    if (that._assemblyManager.isSelectedAssembly(meshId)) {
                                                        pickedAssembly = that._assemblyManager.getSelectedAssemblyID();
                                                    }
                                                }
                                            }

                                            if (!pickedAssembly) {
                                                isSelected = that._selectionManager.selectSingleObject(
                                                    selectedObjectId,
                                                    true,
                                                    true,
                                                    pickResult.type,
                                                    ev.srcEvent
                                                );
                                                that._markedPointsManager.pickMarkedPoint(
                                                    selectedObjectId,
                                                    isSelected,
                                                    true,
                                                    false
                                                );
                                            }
                                        } catch (exc) {
                                        }

                                    } else if (that._selectMode == Enumerations.SelectMode.ALL) {
                                        var selectedObjectId2 = that._meshManager.changeAllMaterialToNewMaterial(
                                            pickResult.info.pickedMesh.id,
                                            "DefaultMaterial"
                                        );
                                    }
                                }
                            }
                        }
                        else {
                            that._selectionManager.unselectAllAssets(true);
                        }
                    }
                    else if (that._pickMode == Enumerations.PickMode.FOCUS) {
                        that.focusCamera(point);
                        eventHander.fireEvent(Enumerations.EventType.CAMERAFOCUSED);
                    }
                    else if (that._pickMode == Enumerations.PickMode.POINT3D) {
                        if (point) {
                            const point3D: IVector3D = { X: point.x, Y: point.y, Z: point.z };
                            const point2D: IVector2D = { X: pointerX, Y: pointerY };
                            const cameraPosition: IVector3D = { X: cam.position.x, Y: cam.position.y, Z: cam.position.z };
                            const eventArgs = { point3D, point2D, cameraPosition };
                            eventHander.fireEvent(Enumerations.EventType.POINT3DCLICKED, eventArgs);
                        }
                    }
                }
                else {
                    if (that._pickMode == Enumerations.PickMode.SELECT) {
                        that._selectionManager.unselectAllAssets(true);
                    }
                }
            }
            finally {
                that._meshManager.setPickableMeshes(false);
            }

        });

        //listen to events...
        mc.on("panstart", function (ev) {
            var canvasRect = that._babEngine.getRenderingCanvasClientRect();

            var hardwareScalingLevel = that._babEngine.getHardwareScalingLevel();
            // event actually likely has the converted coordinates, but this seems to work, so going to keep using this code.
            var pointerX = ev.pointers[0].clientX - canvasRect.left;
            var pointerY = ev.pointers[0].clientY - canvasRect.top;

            that._dragging = true;
            that._draggingXStart = pointerX * hardwareScalingLevel;
            that._draggingYStart = pointerY * hardwareScalingLevel;
        });

        mc.on("panmove", function (ev) {
            if (that._dragging && that._clickMode == Enumerations.ClickMode.REGION) {
                var canvasRect = that._babEngine.getRenderingCanvasClientRect();

                // event actually likely has the converted coordinates, but this seems to work, so going to keep using this code.
                var pointerX = ev.pointers[0].clientX - canvasRect.left;
                var pointerY = ev.pointers[0].clientY - canvasRect.top;

                // The drawing of the box is not based on the hardware scaling to properly draw the region box.
                var hardwareScalingLevel = that._babEngine.getHardwareScalingLevel();
                var startX = that._draggingXStart / hardwareScalingLevel;
                var startY = that._draggingYStart / hardwareScalingLevel;
                var endX = pointerX - startX;
                var endY = pointerY - startY;

                that._drawCanvas2dContext.clearRect(0, 0, that._drawCanvas2dContext.canvas.width, that._drawCanvas2dContext.canvas.height);
                that.drawBox(startX, startY, endX, endY);
            }
        });

        mc.on("panend", function (ev) {

            if (that._clickMode == Enumerations.ClickMode.REGION) {
                var canvasRect = that._babEngine.getRenderingCanvasClientRect();
                var hardwareScalingLevel = that._babEngine.getHardwareScalingLevel();

                // event actually likely has the converted coordinates, but this seems to work, so going to keep using this code.
                var pointerX = (ev.pointers[0].clientX - canvasRect.left) * hardwareScalingLevel;
                var pointerY = (ev.pointers[0].clientY - canvasRect.top) * hardwareScalingLevel;

                if (that._draggingXStart != pointerX && that._draggingYStart != pointerY) {
                    that.view2DBox(Math.min(that._draggingXStart, pointerX), Math.min(that._draggingYStart, pointerY), Math.max(that._draggingXStart, pointerX), Math.max(that._draggingYStart, pointerY));
                }
            }
            that._dragging = false;
        });

        this._onKeyDown = (e: KeyboardEvent) => {
            // ignore when typing in inputs/textareas/contentEditable
            const el = e.target as HTMLElement | null;
            const tag = el?.tagName?.toLowerCase();
            const editing = tag === 'input' || tag === 'textarea' || el?.isContentEditable;

            if ((e.key === 'Escape' || (e as any).keyCode === 27) && !editing) {
                e.preventDefault();
                this._selectionManager?.unselectAllAssets(true);
            }
        };
        window.addEventListener('keydown', this._onKeyDown);


        var that = this;
    }

    dispose() {

        this.resetCoordinators();

        if (this._babScene) {
            this._babScene.dispose();
            this._babScene = null;
        }
        if (this._babEngine) {
            this._babEngine.dispose();
            this._babEngine = null;
        }

        if (this._onKeyDown) {
            window.removeEventListener('keydown', this._onKeyDown);
        }

        this._markedPointsManager = null;
        this._selectionManager = null;
        this._meshManager = null;
        this._displayManager = null;
        this._eventHandler = null;
        if (this._skyBox) this._skyBox.dispose();
        this._skyBox = null;
        if (this._skyBoxTexture) this._skyBoxTexture.dispose();
        this._skyBoxTexture = null;
        this._storedCameraView = null;
        this._drawCanvas2dElement = null;
        this._drawCanvas2dContext = null;
        this._annotationCanvas2dElement = null;
        this._annotationCanvas2dContext = null;
        this._dataRequestCallback = null;
        this._dataRegionRequestCallback = null;
    }

    public rayPick(x: number, y: number): { point: BABYLON.Vector3, valid: boolean } {
        this._meshManager.setPickableMeshes(true);

        var cam = this._babScene.activeCamera as any;
        var cameraViewport = cam.viewport;
        var viewport = cameraViewport.toGlobal(this._babEngine.getRenderWidth(), this._babEngine.getRenderHeight());

        let pickSceneResult = this.pickWithBackFaceExclusion(x, y, viewport, cam);

        this._meshManager.setPickableMeshes(false);

        var pickPointCloudResult = this._pickPointCloud(x, y);

        let pickResult = { point: pickSceneResult.info.pickedPoint, valid: pickSceneResult.info.hit };
        if (pickPointCloudResult != null) {
            if (pickResult.valid) {
                if (cam.position.subtract(pickPointCloudResult).length() < cam.position.subtract(pickResult.point).length()) {
                    return { point: pickPointCloudResult, valid: true };
                }
            }
            else {
                return { point: pickPointCloudResult, valid: true };
            }
        }

        return pickResult;
    }

    /// <summary>This method exists as a custom implementation of BABYLON's pick. This extends babylon's pick to also include a minimum distance from the source pick
    /// point necessary for a mesh to be considered. This is particularly useful when making pick ignore back face picking, when combined with dot product calculation on the normal of the picked surface.</summary>
    private pickWithBackFaceExclusion(x: number, y: number, viewport: BABYLON.Viewport, camera: BABYLON.Camera, predicate?: (mesh: BABYLON.AbstractMesh) => boolean, fastCheck?: boolean): PickingInfo {
        /// <summary>Launch a ray to try to pick a mesh in the scene</summary>
        /// <param name="x">X position on screen</param>
        /// <param name="y">Y position on screen</param>
        /// <param name="predicate">Predicate function used to determine eligible meshes. Can be set to null. In this case, a mesh must be enabled, visible and with isPickable set to true</param>
        /// <param name="fastCheck">Launch a fast check only using the bounding boxes. Can be set to null.</param>
        /// <param name="camera">camera to use for computing the picking ray. Can be set to null. In this case, the scene.activeCamera will be used</param>
        //return this.backFaceExclusionRayIntersection(x, y, viewport, camera, predicate, fastCheck);
        const pickResult = this.pickClosestVisible(x, y,
            {
                camera: this._babScene.activeCamera,
                predicate: predicate,
                fastCheck: fastCheck,
                rejectBackfaces: true
            });

        const pickType = this._labelManager.getLabelData(pickResult?.pickedMesh?.id) != null ? Enumerations.PickObjectType.Annotation : (pickResult?.pickedMesh as any)?.dataSource ?? Enumerations.PickObjectType.Object;
        return { info: pickResult, type: pickType };
    }

    private backFaceExclusionRayIntersection(x: number, y: number, viewport: BABYLON.Viewport, camera: BABYLON.Camera, predicate: (mesh: BABYLON.AbstractMesh) => boolean, fastCheck?: boolean): PickingInfo {
        var pickingInfo: any = new PickingInfo(new BABYLON.PickingInfo(), Enumerations.PickObjectType.Unknown);
        pickingInfo.info.distance = Number.MAX_VALUE;
        var pos: BABYLON.Vector3[] = [];
        for (var meshIndex = 0; meshIndex < this._babScene.meshes.length; meshIndex++) {

            var mesh = this._babScene.meshes[meshIndex];

            if (predicate) {
                if (!predicate(mesh)) {
                    continue;
                }
            } else if (!mesh.isEnabled() || !mesh.isVisible || !mesh.isPickable) {
                continue;
            }

            var world = mesh.getWorldMatrix();
            var ray = this.createPickingRay(x, y, viewport.width, viewport.height, BABYLON.Matrix.Identity(), camera.getViewMatrix(), camera.getProjectionMatrix());

            ray.length = 100000;

            var result = this.intersectsWithBackFaceCheck(ray, mesh, ray.direction, fastCheck);
            if (!result || result.type == Enumerations.PickObjectType.Unknown || !result.info.hit)
                continue;

            if ((!fastCheck && pickingInfo != null && result.info.distance >= pickingInfo.info.distance))
                continue;

            pickingInfo = result;

            if (fastCheck) {
                break;
            }
        }

        return pickingInfo;
    }

    private createPickingRay(x: number, y: number, viewportWidth: number, viewportHeight: number, world: BABYLON.Matrix, view: BABYLON.Matrix, projection: BABYLON.Matrix): BABYLON.Ray {
        var start = BABYLON.Vector3.Unproject(new BABYLON.Vector3(x, y, 0), viewportWidth, viewportHeight, world, view, projection);
        var end = BABYLON.Vector3.Unproject(new BABYLON.Vector3(x, y, 1), viewportWidth, viewportHeight, world, view, projection);

        var direction = end.subtract(start);
        direction.normalize();

        // Make the origin of the picking ray slightly less that the start. 
        return new BABYLON.Ray(start.subtract(direction.scale(.5)), direction);
    }

    private _getSelectedObjectIdFromPick(pickResult: PickingInfo): string {
        if (!pickResult || !pickResult.info || !pickResult.info.pickedMesh) {
            return "-1";
        }

        const explicitObjectId = (pickResult.info as any).__objectId;
        if (explicitObjectId) {
            return explicitObjectId;
        }

        const isFaceMappedType = pickResult.type === Enumerations.PickObjectType.Object
            || pickResult.type === Enumerations.PickObjectType.PagedMesh
            || pickResult.type === Enumerations.PickObjectType.GLBObject;

        if (!isFaceMappedType) {
            return pickResult.info.pickedMesh.id;
        }

        const thinInstanceIndex = pickResult.info.thinInstanceIndex;
        if (thinInstanceIndex != null && thinInstanceIndex >= 0) {
            const thinObjectId = this._meshManager.getObjectIdByThinInstance(pickResult.info.pickedMesh.id, thinInstanceIndex);
            return thinObjectId && thinObjectId !== "-1"
                ? thinObjectId
                : pickResult.info.pickedMesh.id;
        }

        const faceObjectId = this._meshManager.getObjectIdByFace(pickResult.info.pickedMesh.id, pickResult.info.faceId);
        return faceObjectId && faceObjectId !== "-1"
            ? faceObjectId
            : pickResult.info.pickedMesh.id;
    }

    private intersectsWithBackFaceCheck(ray: BABYLON.Ray, mesh: BABYLON.AbstractMesh, pickDirection: BABYLON.Vector3, fastCheck?: boolean): PickingInfo {
        var pickingInfo = new BABYLON.PickingInfo();
        var instance = mesh instanceof BABYLON.InstancedMesh;
        const thinInstance = (mesh as any).hasThinInstances && (mesh as any).thinInstanceEnablePicking;

        if (thinInstance) {
            const thinPick = mesh.intersects(ray, fastCheck);
            if (thinPick && thinPick.hit) {
                return new PickingInfo(thinPick, (mesh as any).dataSource || Enumerations.PickObjectType.Object);
            }

            return new PickingInfo(pickingInfo, Enumerations.PickObjectType.Unknown);
        }

        if (!instance) {
            if (!mesh.subMeshes || !mesh.getBoundingInfo() || !ray.intersectsSphere(mesh.getBoundingInfo().boundingSphere) || !ray.intersectsBox(mesh.getBoundingInfo().boundingBox)) {
                return new PickingInfo(pickingInfo, instance ? Enumerations.PickObjectType.Scan : Enumerations.PickObjectType.Unknown);
            }

            if (!mesh._generatePointsArray()) {
                return new PickingInfo(pickingInfo, instance ? Enumerations.PickObjectType.Scan : Enumerations.PickObjectType.Unknown);
            }
        }


        var intersectInfo: BABYLON.IntersectionInfo = null;

        // Octrees
        var subMeshes: BABYLON.SubMesh[];
        var len: number;

        if (mesh._submeshesOctree && mesh.useOctreeForPicking) {
            var worldRay = BABYLON.Ray.Transform(ray, mesh.getWorldMatrix());
            var intersections = mesh._submeshesOctree.intersectsRay(worldRay);

            len = intersections.length;
            subMeshes = intersections.data;
        } else {
            subMeshes = mesh.subMeshes;
            len = subMeshes.length;
        }

        for (var index = 0; index < len; index++) {
            var subMesh = subMeshes[index];

            // Bounding test
            if (len > 1 && !subMesh.canIntersects(ray))
                continue;

            var currentIntersectInfo = instance ? this.intersectsInstance(mesh, ray, pickDirection) : this.intersectsSubMesh(mesh, subMesh, ray, mesh._positions, mesh.getIndices(), pickDirection, fastCheck);

            var intersects = (currentIntersectInfo != null);
            if (intersects) {
                if ((fastCheck || !intersectInfo || currentIntersectInfo.distance <= intersectInfo.distance)) {
                    intersectInfo = currentIntersectInfo;
                    intersectInfo.subMeshId = index;

                    if (fastCheck) {
                        break;
                    }
                }
            }
        }

        if (intersectInfo) {
            // Get picked point
            var world = mesh.getWorldMatrix();
            var worldOrigin = BABYLON.Vector3.TransformCoordinates(ray.origin, world);
            var direction = ray.direction.clone();
            direction = direction.scale(intersectInfo.distance);
            var worldDirection = BABYLON.Vector3.TransformNormal(direction, world);

            var pickedPoint = worldOrigin.add(worldDirection);

            // Return result
            pickingInfo.hit = true;

            pickingInfo.distance = intersectInfo.distance;
            pickingInfo.pickedPoint = instance ? pickedPoint.subtract(mesh.position) : pickedPoint;

            pickingInfo.pickedMesh = mesh;
            pickingInfo.bu = intersectInfo.bu;
            pickingInfo.bv = intersectInfo.bv;
            pickingInfo.faceId = intersectInfo.faceId;
            pickingInfo.subMeshId = intersectInfo.subMeshId;
            pickingInfo.getNormal();
            return new PickingInfo(pickingInfo, instance ? Enumerations.PickObjectType.Scan : (mesh as any).dataSource);
        }

        return new PickingInfo(pickingInfo, instance ? Enumerations.PickObjectType.Scan : Enumerations.PickObjectType.Object);
    }

    // BABYLON OVERRIDE CODE
    // Detects whethe ray intersects a mesh. Additional logic here to make sure a "backface" is not picked by comparing normal and the pick direction.
    private intersectsSubMesh(mesh: BABYLON.AbstractMesh, subMesh: BABYLON.SubMesh, ray: BABYLON.Ray, positions: BABYLON.Vector3[], indices: any, pickDirection: BABYLON.Vector3, fastCheck?: boolean): BABYLON.IntersectionInfo {
        var intersectInfo: BABYLON.IntersectionInfo = null;

        // Triangles test
        for (var index = subMesh.indexStart; index < subMesh.indexStart + subMesh.indexCount; index += 3) {
            var p0 = positions[indices[index]];
            var p1 = positions[indices[index + 1]];
            var p2 = positions[indices[index + 2]];

            var currentIntersectInfo = ray.intersectsTriangle(p0, p1, p2);
            if (currentIntersectInfo) {

                // Checks if the point is behind the camera. .45 is used as an observed value when the minZ was changed to .1. If we use 0, there are cases where the 
                // camera picks points that appear "behind" the camera. Therefore, since the issue may be due to rounding, points extremely close to the camera are considered "behind".
                if (currentIntersectInfo.distance < .45) {
                    continue;
                }

                if ((fastCheck || !intersectInfo || currentIntersectInfo.distance < intersectInfo.distance)) {

                    var normal = this.getNormal(mesh, index / 3, currentIntersectInfo.bu, currentIntersectInfo.bv);

                    var dot = BABYLON.Vector3.Dot(pickDirection, normal);

                    if (dot <= 0) {
                        intersectInfo = currentIntersectInfo;
                        intersectInfo.faceId = index / 3;

                        if (fastCheck) {
                            break;
                        }
                    }
                }
            }
        }

        return intersectInfo;
    }

    // BABYLON OVERRIDE CODE
    // Detects whethe ray intersects an instance. Additional logic here to make sure a "backface" is not picked by comparing normal and the pick direction.
    private intersectsInstance(mesh: BABYLON.AbstractMesh, ray: BABYLON.Ray, pickDirection: BABYLON.Vector3, fastCheck?: boolean): BABYLON.IntersectionInfo {

        var tm = BABYLON.TmpVectors.Matrix[0];

        mesh.getWorldMatrix().invertToRef(tm);

        if ((ray as any)._tmpRay) {
            BABYLON.Ray.TransformToRef(ray, tm, (ray as any)._tmpRay);
        } else {
            (ray as any)._tmpRay = BABYLON.Ray.Transform(ray, tm);
        }

        return this.intersectsInstanceInternal(mesh, (ray as any)._tmpRay, fastCheck);
    }

    private intersectsInstanceInternal(mesh: BABYLON.AbstractMesh, ray: BABYLON.Ray, fastCheck?: boolean): BABYLON.IntersectionInfo {

        const intersectionThreshold = mesh.getClassName() === "InstancedLinesMesh" || mesh.getClassName() === "LinesMesh" ? (this as any).intersectionThreshold : 0;
        const boundingInfo = mesh.getBoundingInfo();
        if (!mesh.subMeshes || !boundingInfo || !ray.intersectsSphere(boundingInfo.boundingSphere) || !ray.intersectsBox(boundingInfo.boundingBox)) {
            return intersectInfo;
        }

        if (!mesh._generatePointsArray()) {
            return intersectInfo;
        }

        var intersectInfo: BABYLON.Nullable<BABYLON.IntersectionInfo> = null;

        var subMeshes = this._babScene.getIntersectingSubMeshCandidates(mesh, ray);
        var len: number = subMeshes.length;
        for (var index = 0; index < len; index++) {
            var subMesh = subMeshes.data[index];

            // Bounding test
            if (len > 1 && !subMesh.canIntersects(ray)) {
                continue;
            }

            var currentIntersectInfo = subMesh.intersects(ray, (<BABYLON.Vector3[]>mesh._positions),
                (<BABYLON.IndicesArray>mesh.getIndices()), fastCheck);

            if (currentIntersectInfo) {
                if (fastCheck || !intersectInfo || currentIntersectInfo.distance < intersectInfo.distance) {

                    var normal = this.getNormal(mesh, currentIntersectInfo.faceId, currentIntersectInfo.bu, currentIntersectInfo.bv);
                    var dot = BABYLON.Vector3.Dot(ray.direction, normal);
                    if (dot <= 0) {
                        intersectInfo = currentIntersectInfo;
                        intersectInfo.subMeshId = index;

                        if (fastCheck) {
                            break;
                        }
                    }
                }
            }
        }

        return intersectInfo;
    }

    private getNormal(mesh: BABYLON.AbstractMesh, faceId: number, bu: number, bv: number, useWorldCoordinates = false, useVerticesNormals = true): BABYLON.Vector3 {
        if (!mesh || !mesh.isVerticesDataPresent(BABYLON.VertexBuffer.NormalKind)) {
            return null;
        }

        var indices = mesh.getIndices();
        var result: BABYLON.Vector3;

        if (useVerticesNormals) {
            var normals = mesh.getVerticesData(BABYLON.VertexBuffer.NormalKind);

            var normal0 = BABYLON.Vector3.FromArray(normals, indices[faceId * 3] * 3);
            var normal1 = BABYLON.Vector3.FromArray(normals, indices[faceId * 3 + 1] * 3);
            var normal2 = BABYLON.Vector3.FromArray(normals, indices[faceId * 3 + 2] * 3);

            normal0 = normal0.scale(bu);
            normal1 = normal1.scale(bv);
            normal2 = normal2.scale(1.0 - bu - bv);

            result = new BABYLON.Vector3(normal0.x + normal1.x + normal2.x, normal0.y + normal1.y + normal2.y, normal0.z + normal1.z + normal2.z);
        } else {
            var positions = mesh.getVerticesData(BABYLON.VertexBuffer.PositionKind);

            var vertex1 = BABYLON.Vector3.FromArray(positions, indices[faceId * 3] * 3);
            var vertex2 = BABYLON.Vector3.FromArray(positions, indices[faceId * 3 + 1] * 3);
            var vertex3 = BABYLON.Vector3.FromArray(positions, indices[faceId * 3 + 2] * 3);

            var p1p2 = vertex1.subtract(vertex2);
            var p3p2 = vertex3.subtract(vertex2);

            result = BABYLON.Vector3.Cross(p1p2, p3p2);
        }

        if (useWorldCoordinates) {
            result = BABYLON.Vector3.TransformNormal(result, mesh.getWorldMatrix());
        }

        return BABYLON.Vector3.Normalize(result);
    }
    resize() {
        this._babEngine.resize();
        this._babScene.render(); // force an immediate repaint right after resizing

        var camera = this.scene.activeCamera as AnyCam;

        if (isExamineCamera(camera)) {
            camera._angularSensibilityX = window.innerWidth / Math.PI;
            camera._angularSensibilityY = window.innerHeight / Math.PI;

            camera.heightScale = this._babEngine.getRenderHeight() / this._babEngine.getRenderWidth();
            camera.widthScale = this._babEngine.getRenderWidth() / this._babEngine.getRenderHeight();

            camera.updateMode(camera.mode);
        }
        else if (isUniversalCamera(camera)) {
            camera._angularSensibilityX = window.innerWidth / Math.PI;
            camera._angularSensibilityY = window.innerHeight / Math.PI;
        }
    }

    drawMarkedPoints() {
        var mps = this._markedPointsManager.markedPoints;
        var labels = this._markedPointsManager.labels;
        var measurements = this._markedPointsManager.measurements;
        this._annotationCanvas2dContext.clearRect(0, 0, this._annotationCanvas2dContext.canvas.width, this._annotationCanvas2dContext.canvas.height);

        var keys = Object.keys(mps);


        var cam = this.scene.activeCamera;

        for (var i = 0; i < keys.length; i++) {

            

            if (mps[keys[i]] != null) {
                mps[keys[i]].render(this._babScene.getTransformMatrix(), cam.viewport.toGlobal(this._babEngine.getRenderWidth(), this._babEngine.getRenderHeight()));
            }
        }

        for (var i = 0; i < labels.length; i++) {

            labels[i].render(cam.viewport.toGlobal(this._babEngine.getRenderWidth(), this._babEngine.getRenderHeight()));
        }
        for (var i = 0; i < measurements.length; i++) {

            measurements[i].render(this._babScene.getTransformMatrix(), cam.viewport.toGlobal(this._babEngine.getRenderWidth(), this._babEngine.getRenderHeight()));
        }
    }

    drawRectangleMarkedPoint(xPosition: number, yPosition: number) {


    }

    drawBox(startX: number, startY: number, endX: number, endY: number) {

        var prevStrokeStyle = this._drawCanvas2dContext.strokeStyle;
        this._drawCanvas2dContext.strokeStyle = "#000000";
        this._drawCanvas2dContext.fillStyle = "rgba(0, 0, 255, .25)";
        this._drawCanvas2dContext.lineWidth = 1;
        this._drawCanvas2dContext.fillRect(startX, startY, endX, endY);
        this._drawCanvas2dContext.strokeRect(startX, startY, endX, endY);
        this._drawCanvas2dContext.strokeStyle = prevStrokeStyle;
    }

    setCameraPosition(position: BABYLON.Vector3) {
        
        var cam = this.scene.activeCamera as AnyCam;

        if (isExamineCamera(cam)) {
            cam.position = position;

            cam.computeViewMatrix();
        }
        else if (isUniversalCamera(cam)) {
            cam.position = position;

            cam.computeViewMatrix();
        }
    }

    setCameraMouseZoomFactor(factor: number) {

        var camera = this.scene.activeCamera as AnyCam;
        if (isExamineCamera(camera)) {
            camera.mouseZoomFactor = factor;
        }
        else if (isUniversalCamera(camera)) {
            camera.mouseZoomFactor = factor;
        }
    }

    setCamraMouseWheelFactor(factor: number) {
        var camera = this.scene.activeCamera as AnyCam;
        if (isExamineCamera(camera)) {
            camera.mouseWheelFactor = factor;
        }
        else if (isUniversalCamera(camera)) {
            camera.mouseWheelFactor = factor;
        }
    }

    setCameraPinchFactor(factor: number) {
        var camera = this.scene.activeCamera as AnyCam;
        if (isExamineCamera(camera)) {
            camera.pinchFactor = factor;
        }
        else if (isUniversalCamera(camera)) {
            camera.pinchFactor = factor;
        }
    }
    setAllowPick(allow: boolean) {
        this._allowPick = allow;
    }

    setCameraDirection(position: BABYLON.Vector3, quaternion: BABYLON.Quaternion) {
        const cam = this.scene.activeCamera as AnyCam;
        if (!cam) return;

        const cosVal = Math.cos(quaternion.w);
        const sinVal = Math.sin(quaternion.w);

        // Basis extraction (kept as you wrote it)
        const Xt = new BABYLON.Vector3(
            cosVal + (quaternion.x * quaternion.x) * (1 - cosVal),
            quaternion.z * sinVal + quaternion.x * quaternion.y * (1 - cosVal),
            -quaternion.y * sinVal + quaternion.x * quaternion.z * (1 - cosVal)
        );
        const Yt = new BABYLON.Vector3(
            quaternion.x * quaternion.y * (1 - cosVal) - quaternion.z * sinVal,
            cosVal + quaternion.y * quaternion.y * (1 - cosVal),
            quaternion.x * sinVal + quaternion.y * quaternion.z * (1 - cosVal)
        );
        const Zt = new BABYLON.Vector3(
            quaternion.y * sinVal + quaternion.x * quaternion.z * (1 - cosVal),
            -quaternion.x * sinVal + quaternion.y * quaternion.z * (1 - cosVal),
            cosVal + quaternion.z * quaternion.z * (1 - cosVal)
        );

        const isOrtho = (cam.mode === BABYLON.Camera.ORTHOGRAPHIC_CAMERA);
        const focalDistance = 0.1;
        const camDirection = Zt.scale(-1);

        const camTarget = position.subtract(camDirection.scale(Math.abs(focalDistance)));

        // ------------------------------------------------------------
        // ExamineCamera branch
        // ------------------------------------------------------------
        if (isExamineCamera(cam as any)) {
            const ex: any = cam;

            if (isOrtho) {
                ex._currentTarget = camTarget;
                ex.setPosition(camTarget.add(camDirection.scale(Math.abs(focalDistance))));
                ex.minZ = -10000;
            } else {
                ex.radiusToOrthoScale = 1;
                ex._currentTarget = camTarget;
                ex.setPosition(position);
                ex.minZ = 0.5;
            }

            ex.computeViewMatrix(true);
            this._sceneDirty = true;
            return;
        }

        // ------------------------------------------------------------
        // UniversalCamera branch
        // ------------------------------------------------------------
        if (isUniversalCamera(cam as any)) {
            const fly: any = cam;
            fly.position.copyFrom(position);

            fly.rotation.copyFrom(quaternion.toEulerAngles());
            fly.rotationQuaternion = null;

            // Keep target coherent
            if (typeof fly.setTarget === "function") fly.setTarget(camTarget);
            else fly.target = camTarget.clone();

            fly.minZ = isOrtho ? -10000 : 0.5;

            fly.getViewMatrix(true);
            fly.getProjectionMatrix(true);
            fly.computeWorldMatrix?.(true);

            this._sceneDirty = true;
            return;
        }

        // ------------------------------------------------------------
        // Fallback: unknown camera type
        // ------------------------------------------------------------
        {
            const anyCam: any = cam;
            anyCam.position.copyFrom(position);
            if (typeof anyCam.setTarget === "function") anyCam.setTarget(camTarget);
            anyCam.minZ = isOrtho ? -10000 : 0.5;
            this._sceneDirty = true;
        }
    }


    getCameraProperties() {
        const cam = this.scene.activeCamera as AnyCam;
        const target = this.getCamTarget(cam);
        const focalDistance = cam.position.subtract(target).length();

        // "cameraHeight" meaning differs; keep old behavior for Examine, best-effort for Fly
        let cameraHeight = 0;
        if (isExamineCamera(cam)) {
            cameraHeight = cam.radiusToOrthoScale * focalDistance * 2;
        } else if(isUniversalCamera(cam)) {
            
            const c: any = cam;
            if (cam.mode === BABYLON.Camera.ORTHOGRAPHIC_CAMERA && c.orthoTop != null && c.orthoBottom != null) {
                cameraHeight = Math.abs(c.orthoTop - c.orthoBottom);
            } else {
                cameraHeight = focalDistance * 2; // fallback
            }
        }

        const view = cam.getViewMatrix();
        const q = BABYLON.Quaternion.FromRotationMatrix(view);

        return {
            CameraFocalDistance: focalDistance,
            CameraHeight: cameraHeight,
            CameraPosition: cam.position.clone(),
            Quaternion: q,
            CameraType: (cam.mode === BABYLON.Camera.ORTHOGRAPHIC_CAMERA)
                ? Enumerations.CameraMode.ORTHOGRAPHIC
                : Enumerations.CameraMode.PERSPECTIVE
        };
    }

    setFollowNavMode(free: boolean) {
        if (this._followCamera == null)
            return;

        this._followCamera.tweenCameraMode = !free;
    }
    enterFollowCamera(routeId: string, junctionId: string, exitCameraCallback: () => void) {

        // If we ever reimplement follow camera, note that it needs to use active camera instead of cameras[0]
        if (this._followCamera != null)
            this.exitFollowCamera();

        var that = this;
        for (var x = 0; x < this._routes.length; x++) {
            if (this._routes[x].id == routeId) {

                this._followCamera = new FollowCamera("Follow Camera", this._eventHandler, junctionId, this._routes[x], this._materialManager, () => {
                    that._followCamera.disposeHeadlights();
                    that._followCamera.detachControl(that._babEngine.getRenderingCanvas() as any);
                    that._babScene.removeCamera(that._followCamera);
                    that._followCamera.dispose();
                    that._followCamera = null;
                    that.addHeadlights(that._examineCamera);
                    that._examineCamera.attachControl(that._babEngine.getRenderingCanvas() as any, true, false);
                    that._examineCamera._getViewMatrix();
                    exitCameraCallback();
                }, this._babScene);

                (<ExamineCamera>this.scene.cameras[0]).disposeHeadlights()
                this.addHeadlights(this._followCamera);
                this._babScene.switchActiveCamera(this._followCamera);

                if (this._edgeProcess != null) {
                    setupScreenSpaceCurvature(this._babScene, this._followCamera, this._edgeRidge, this._edgeValley);
                }

                break;
            }
        }
    }

    private get isHeadlightMode(): boolean {
        return (!this._hemispericLights);
    }

    setEdgeMode(enabled: boolean): void {

        this._shaderEnabled = enabled;

        if (this._examineCamera.mode != BABYLON.Camera.ORTHOGRAPHIC_CAMERA) {
            this.applyEdgeShader(this.scene.activeCamera);
        }
    }

    private _updateEdgeProcessRenderList() {
        if (!this._edgeProcess || !this._babScene.geometryBufferRenderer)
            return;

        const meshesArray = this._meshManager.getAllMeshes();
        const glbMeshesArray = this._assemblyManager.getAllMeshes();
        if (this._lastProcessedMeshesArrayVersion !== meshesArray.version || this._lastProcessedGlbMeshesArrayVersion !== glbMeshesArray.version) {
            const allMeshes = meshesArray.array.concat(glbMeshesArray.array);
            this._babScene.geometryBufferRenderer.renderList = allMeshes;

            this._lastProcessedMeshesArrayVersion = meshesArray.version;
            this._lastProcessedGlbMeshesArrayVersion = glbMeshesArray.version;
        }
    }

    applyEdgeShader(cam: BABYLON.Camera): void {
        if (!cam) return;

        const hasEdgePP = (): boolean => {
            const arr = (cam as any)._postProcesses as any[] | undefined;
            if (!arr || !this._edgeProcess) return false;
            return arr.indexOf(this._edgeProcess as any) !== -1;
        };

        if (this._shaderEnabled) {
            if (!this._edgeProcess) {
                this._edgeProcess = setupScreenSpaceCurvature(
                    this._babScene,
                    cam,
                    this._edgeRidge,
                    this._edgeValley
                );
            }

            if (this._edgeProcess && !hasEdgePP()) {
                cam.attachPostProcess(this._edgeProcess);
            }

            return;
        }

        if (this._edgeProcess && hasEdgePP()) {
            cam.detachPostProcess(this._edgeProcess);
        }

        this._lastProcessedMeshesArrayVersion = 0;
        this._lastProcessedGlbMeshesArrayVersion = 0;
    }
    setAmbientIntensity(intensity: number): void {
        this._ambientIntensity = intensity;

        if (this._hemispericLights != null && this._hemispericLights.length == 2) {
            this._hemispericLights[0].intensity = this._ambientIntensity;
            this._hemispericLights[1].intensity = this._ambientIntensity - .4;
        }
    }

    setShaderValley(valley: number): void {

        this._edgeValley = valley;

        if (this._edgeProcess != null) {
            (this._edgeProcess as any).valley = valley;
        }
    }
    setShaderRidge(ridge: number): void {

        this._edgeRidge = ridge;
        if (this._edgeProcess != null) {
            (this._edgeProcess as any).ridge = ridge;
        }
    }
    getShaderEnabled(): boolean {
        return this._shaderEnabled;
    }
    getShaderValley(): number {
        return this._edgeValley;
    }
    getShaderRidge(): number {
        return this._edgeRidge;
    }
    getAmbientIntensity(): number {
        return this._ambientIntensity;
    }
    setLightingMode(mode: Enumerations.LightingMode): void {
        const scene = this._babScene;
        const camera = this.scene.activeCamera as ExamineCamera;
        if (mode === "ambient") {
            if (!this._hemispericLights) {
                if (camera && camera.hasHeadlights()) {
                    camera.disposeHeadlights();
                }

                // This is the default hemispheric light pointing "up"
                const lightUp = new BABYLON.HemisphericLight("upLight", BABYLON.Vector3.Up(), scene);
                lightUp.diffuse = new BABYLON.Color3(0.8, 0.8, 0.9); // This makes the scene have a faint blue touch
                lightUp.specular = new BABYLON.Color3(0.15, 0.15, 0.15); // Very limited reflections
                lightUp.intensity = this._ambientIntensity;

                // A second hemispheric light is added to ensure correct shading of edges.
                const lightRight = new BABYLON.HemisphericLight("rightLight", BABYLON.Vector3.Right(), scene);
                lightRight.diffuse = lightUp.diffuse;
                lightRight.specular = BABYLON.Color3.Black(); // No specular highlights for the secondary light
                lightRight.intensity = .02;

                this._hemispericLights = [lightUp, lightRight];

                scene.ambientColor = new BABYLON.Color3(.45, .45, .45);
                scene.imageProcessingConfiguration.contrast = 1.2;
                scene.imageProcessingConfiguration.exposure = 0.8;
                scene.imageProcessingConfiguration.toneMappingEnabled = true;
            }
        }
        else {
            if (camera && !camera.hasHeadlights()) {
                if (this._hemispericLights) {
                    this._hemispericLights.forEach(light => {
                        scene.removeLight(light);
                        light.dispose();
                    });
                    this._hemispericLights = null;
                }

                scene.ambientColor = new BABYLON.Color3(.4, .4, .4);
                scene.imageProcessingConfiguration.contrast = 1.0;
                scene.imageProcessingConfiguration.exposure = 1.0;
                scene.imageProcessingConfiguration.toneMappingEnabled = false;

                this.addHeadlights(camera);

                camera._getViewMatrix();
            }
        }
    }

    addHeadlights(camera: ExamineCamera | FollowCamera | UniversalCamera) {
        if (this.isHeadlightMode) {
            var intensity = .6;

            var frontLight = new BABYLON.DirectionalLight("FrontLight", new BABYLON.Vector3(-1, 0, 0), this._babScene);
            frontLight.falloffType = 3;
            frontLight.intensity = intensity;

            camera.setHeadlight(frontLight);
        }
    }

    focusFollowCamera() {
        if (this._followCamera != null) {
            this._followCamera.focusCanvas();
        }
    }

    exitFollowCamera() {

        this._followCamera.exitCamera();
        this._followCamera = null;
    }
    zoomToJunction(routeId: string, junctionId: string) {
        for (var x = 0; x < this._routes.length; x++) {
            if (this._routes[x].id == routeId) {
                for (var y = 0; y < this._routes[x].junctions.length; y++) {
                    if (this._routes[x].junctions[y].id == junctionId) {
                        this.zoomToExtents(this._routes[x].junctions[y].position, this._routes[x].junctions[y].position);
                    }
                }
            }
        }
    }

    hideRoute(route: any) {

        if (this._followCamera != null) {
            if (this._followCamera.getRouteID() == route.ID) {
                this.exitFollowCamera();
            }
        }

        for (var x = 0; x < this._routes.length; x++) {
            if (this._routes[x].id == route.ID) {
                for (var y = 0; y < this._routes[x].meshes.length; y++) {

                    this._routes[x].meshes[y].dispose();
                }

                this._routes.splice(x, 1);
            }
        }
    }

    displayRoute(route: any) {

        var pathMeshes: { [pathId: string]: BABYLON.Mesh[]; } = {};

        {
            var newRoute: Interfaces.Interfaces.IFollowCameraRoute = {
                id: route.ID, junctions: [], meshes: []
            };
            this._routes.push(newRoute);

            for (var x = 0; x < route.Junctions.length; x++) {
                var junction = route.Junctions[x];
                var junc: Interfaces.Interfaces.IJunction = { id: junction.ID, junctionMesh: null, paths: [], position: new BABYLON.Vector3(junction.Position.X / 1000, junction.Position.Y / 1000, junction.Position.Z / 1000) };

                newRoute.junctions.push(junc);
                var b1 = BABYLON.Mesh.CreateBox(junc.id, 1, this._babScene);
                b1.position = junc.position;
                b1.material = junction.IsWayPoint ? this._materialManager.getMaterial("WaypointMaterial") : this._materialManager.getMaterial("JunctionMaterial");
                b1.scaling.y = .2;
                b1.scaling.x = junction.Width / 1000 + .1;
                b1.scaling.z = junction.Width / 1000 + .1;
                junc.junctionMesh = b1;
                newRoute.meshes.push(b1);
            }

            for (var x = 0; x < route.Paths.length; x++) {

                var pathId = route.Paths[x].ID;
                var newPath: Interfaces.Interfaces.IPath = { id: pathId, meshes: [], wayPoints: [] };

                var start = this.getJunction(newRoute, route.Paths[x].StartJunctionID);
                var end = this.getJunction(newRoute, route.Paths[x].EndJunctionID);

                // create paths from this junction
                for (var y = 0; y < route.Paths[x].Segments.length; y++) {

                    var createMeshes = !(Object.keys(pathMeshes).indexOf(pathId) >= 0);
                    var segment = route.Paths[x].Segments[y];

                    var middlePos = new BABYLON.Vector3((segment.StartPosition.X + (segment.EndPosition.X - segment.StartPosition.X) / 2) / 1000, (segment.StartPosition.Y + (segment.EndPosition.Y - segment.StartPosition.Y) / 2) / 1000, (segment.StartPosition.Z + (segment.EndPosition.Z - segment.StartPosition.Z) / 2) / 1000);
                    var startPos = new BABYLON.Vector3(segment.StartPosition.X / 1000, segment.StartPosition.Y / 1000, segment.StartPosition.Z / 1000);
                    var EndPos = new BABYLON.Vector3(segment.EndPosition.X / 1000, segment.EndPosition.Y / 1000, segment.EndPosition.Z / 1000);

                    if (y == 0) {
                        var newWP: Interfaces.Interfaces.IWaypoint = { id: segment.ID, position: startPos, type: Enumerations.WaypointType.PATH };
                        newPath.wayPoints.push(newWP);
                    }

                    var newWP: Interfaces.Interfaces.IWaypoint = { id: segment.ID, position: EndPos, type: Enumerations.WaypointType.PATH };
                    newPath.wayPoints.push(newWP);


                    {
                        {
                            var directionVector = new BABYLON.Vector3((segment.EndPosition.X - segment.StartPosition.X) / 1000, (segment.EndPosition.Y - segment.StartPosition.Y) / 1000, (segment.EndPosition.Z - segment.StartPosition.Z) / 1000);

                            var pathLen = directionVector.length();

                            var p1 = BABYLON.Mesh.CreateBox(segment.ID, 1, this._babScene);
                            p1.position = middlePos;
                            p1.scaling.y = pathLen;
                            p1.scaling.z = .1;
                            p1.scaling.x = segment.Width / 1000;
                            p1.alignWithNormal(directionVector, BABYLON.Vector3.Cross(new BABYLON.Vector3(0, 1, 0), directionVector));
                            p1.material = this._materialManager.getMaterial("PathMaterial");
                            newRoute.meshes.push(p1);
                            newPath.meshes.push(p1);
                        }
                    }
                }

                if (start != null) {
                    var startPath: Interfaces.Interfaces.IPath = { id: pathId, meshes: [], wayPoints: [] };
                    startPath.meshes = newPath.meshes;

                    var startWP: Interfaces.Interfaces.IWaypoint = { id: route.Paths[x].StartJunctionID, position: new BABYLON.Vector3(start.position.x, start.position.y, start.position.z), type: Enumerations.WaypointType.JUNCTION };
                    startPath.wayPoints.push(startWP);

                    for (var i = 0; i < newPath.wayPoints.length; i++) {
                        startPath.wayPoints.push(newPath.wayPoints[i]);
                    }

                    if (end != null) {
                        var endWP: Interfaces.Interfaces.IWaypoint = { id: route.Paths[x].EndJunctionID, position: new BABYLON.Vector3(end.position.x, end.position.y, end.position.z), type: Enumerations.WaypointType.JUNCTION };
                        startPath.wayPoints.push(endWP);
                    }
                    start.paths.push(startPath);
                }
                if (end != null) {
                    var revPath: Interfaces.Interfaces.IPath = { id: pathId, meshes: [], wayPoints: [] };
                    revPath.meshes = newPath.meshes.reverse();

                    var endWP: Interfaces.Interfaces.IWaypoint = { id: route.Paths[x].EndJunctionID, position: new BABYLON.Vector3(end.position.x, end.position.y, end.position.z), type: Enumerations.WaypointType.JUNCTION };
                    revPath.wayPoints.push(endWP);

                    for (var i = newPath.wayPoints.length - 1; i > 0; i--) {
                        revPath.wayPoints.push(newPath.wayPoints[i]);
                    }

                    if (start != null) {
                        var startWP: Interfaces.Interfaces.IWaypoint = { id: route.Paths[x].StartJunctionID, position: new BABYLON.Vector3(start.position.x, start.position.y, start.position.z), type: Enumerations.WaypointType.JUNCTION };
                        revPath.wayPoints.push(startWP);
                    }

                    end.paths.push(revPath);
                }
            }
        }
    }


    getJunction(route: Interfaces.Interfaces.IFollowCameraRoute, nodeId: string): Interfaces.Interfaces.IJunction {

        for (var x = 0; x < route.junctions.length; x++) {
            if (route.junctions[x].id == nodeId)
                return route.junctions[x];
        }
        return null;
    }
    setCameraPropertiesWithQuaternion(
        position: BABYLON.Vector3,
        quaternion: BABYLON.Quaternion,
        focalDistance: number,
        cameraHeight: number,
        mode: Enumerations.CameraMode
    ) {
        focalDistance = Math.max(0.001, Math.abs(focalDistance));

        const cameraView = new BABYLON.Matrix();
        quaternion.toRotationMatrix(cameraView);

        const camDirection = new BABYLON.Vector3(
            cameraView.m[2] * -1,
            cameraView.m[6] * -1,
            cameraView.m[10] * -1
        );

        const cam = this.scene.activeCamera as AnyCam;
        if (!cam) return;

        const isOrtho = (mode === Enumerations.CameraMode.ORTHOGRAPHIC);

        const camTar = position.subtract(camDirection.scale(focalDistance));

        // ------------------------------------------------------------
        // ExamineCamera branch
        // ------------------------------------------------------------
        if (isExamineCamera(cam as any)) {
            const ex: any = cam;

            this.setCameraMode(mode);

            if (isOrtho) {
                ex.radiusToOrthoScale = (cameraHeight / 2) / focalDistance;

                ex._currentTarget = camTar;
                ex.setPosition(camTar.add(camDirection.scale(focalDistance)));

                ex.minZ = -10000;
            } else {
                ex.radiusToOrthoScale = 1;

                ex._currentTarget = camTar;
                ex.setPosition(position);

                ex.minZ = 0.5;
            }

            this.addHeadlights(ex);

            ex.getProjectionMatrix(true);
            ex.updateCache(true);

            ex.computeViewMatrix(true);
            ex.zoomFactor = 1;
            ex.panningSpeed = 1;

            ex.computeViewMatrix(true);
            ex._getViewMatrix?.();

            this._sceneDirty = true;
            return;
        }

        // ------------------------------------------------------------
        // UniversalCamera branch
        // ------------------------------------------------------------
        if (isUniversalCamera(cam as any)) {
            const fly: any = cam;

            // Projection
            if (isOrtho) {
                fly.mode = BABYLON.Camera.ORTHOGRAPHIC_CAMERA;

                const halfH = cameraHeight / 2;
                const halfW = halfH; 

                fly.orthoLeft = -halfW;
                fly.orthoRight = halfW;
                fly.orthoTop = halfH;
                fly.orthoBottom = -halfH;

                fly.minZ = -10000;
            } else {
                fly.mode = BABYLON.Camera.PERSPECTIVE_CAMERA;
                fly.minZ = 0.5;
            }

            // Position
            fly.position.copyFrom(position);

            fly.rotation.copyFrom(quaternion.toEulerAngles());
            fly.rotationQuaternion = null;

            if (typeof fly.setTarget === "function") fly.setTarget(camTar);
            else fly.target = camTar.clone();

            if (fly.inertialMoveForward !== undefined) fly.inertialMoveForward = 0;
            if (fly.inertialMoveRight !== undefined) fly.inertialMoveRight = 0;
            if (fly.inertialMoveUp !== undefined) fly.inertialMoveUp = 0;
            if (fly.inertialAlphaOffset !== undefined) fly.inertialAlphaOffset = 0;
            if (fly.inertialBetaOffset !== undefined) fly.inertialBetaOffset = 0;

            this.addHeadlights(fly);

            fly.getProjectionMatrix(true);
            fly.getViewMatrix(true);
            fly.computeWorldMatrix?.(true);

            this._sceneDirty = true;
            return;
        }

        // ------------------------------------------------------------
        // Fallback: unknown camera type
        // ------------------------------------------------------------
        {
            const anyCam: any = cam;
            anyCam.position?.copyFrom?.(position);
            if (typeof anyCam.setTarget === "function") anyCam.setTarget(camTar);
            this._sceneDirty = true;
        }
    }


    setCameraPropertiesWithTarget(position: BABYLON.Vector3, cameraTarget: BABYLON.Vector3, cameraAlpha: number, focalDistance: number, cameraHeight: number, mode: Enumerations.CameraMode) {

        focalDistance = Math.abs(focalDistance);
        var camera = this.scene.cameras[0] as unknown as ExamineCamera;

        camera.mode = mode;


        if (mode == Enumerations.CameraMode.ORTHOGRAPHIC) {

            focalDistance = (cameraHeight / 2) / .51;
            camera.radiusToOrthoScale = (cameraHeight / 2) / (focalDistance);
            camera._currentTarget = (cameraTarget);
            camera.setPosition(position);

            // Keep an eye on this line. Problems with picking arose and this line fixes them by setting the projection matrix differently. 
            // I am not 100 percent that this is the correct factor to use, but has so far worked.
            // You can see the effects of this change in the camera's projection matrix. Incorrect settings of this will cause picks to not hit
            // meshes that are clearly in the frustum (and possibly lighting issues). Note that this is only applicable to be set in ortho mode.
            camera.minZ = -10000;
        }
        else {

            camera.radiusToOrthoScale = 1;

            camera._currentTarget = cameraTarget;
            camera.setPosition(position);
            camera.minZ = .5;
        }

        this.addHeadlights(camera);
        camera.alpha = cameraAlpha;
        camera.getProjectionMatrix(true)
        camera.updateCache(true);

        camera.computeViewMatrix(true);
        this.setCameraMode(mode);
        camera.zoomFactor = 1;
        camera.panningSpeed = 1;

        this._sceneDirty = true;
    }
    initializeHardCodedCamera(position: BABYLON.Vector3, quaternion: BABYLON.Quaternion, focalDistance: number, cameraHeight: number, mode: Enumerations.CameraMode) {
    }

    initializeCameraProperties(
        position: BABYLON.Vector3,
        quaternion: BABYLON.Quaternion,
        focalDistance: number,
        cameraHeight: number,
        mode: Enumerations.CameraMode
    ) {
        const cosVal = Math.cos(quaternion.w);
        const sinVal = Math.sin(quaternion.w);

        focalDistance = Math.max(0.001, Math.abs(focalDistance));

        const Xt = new BABYLON.Vector3(
            cosVal + (quaternion.x * quaternion.x) * (1 - cosVal),
            quaternion.z * sinVal + quaternion.x * quaternion.y * (1 - cosVal),
            -quaternion.y * sinVal + quaternion.x * quaternion.z * (1 - cosVal)
        );

        const Yt = new BABYLON.Vector3(
            quaternion.x * quaternion.y * (1 - cosVal) - quaternion.z * sinVal,
            cosVal + quaternion.y * quaternion.y * (1 - cosVal),
            quaternion.x * sinVal + quaternion.y * quaternion.z * (1 - cosVal)
        );

        const Zt = new BABYLON.Vector3(
            quaternion.y * sinVal + quaternion.x * quaternion.z * (1 - cosVal),
            -quaternion.x * sinVal + quaternion.y * quaternion.z * (1 - cosVal),
            cosVal + quaternion.z * quaternion.z * (1 - cosVal)
        );

        const camera = this.scene.activeCamera as AnyCam;

        // ---------------------------------------------------------------------
        // ExamineCamera branch
        // ---------------------------------------------------------------------
        if (isExamineCamera(camera)) {
            camera.mode = mode;

            const camDirection = Yt;

            if (mode == Enumerations.CameraMode.ORTHOGRAPHIC) {
                const camTar = position.subtract(camDirection.scale(Math.abs(focalDistance)));

                focalDistance = (cameraHeight / 2) / 0.51;
                camera.radiusToOrthoScale = (cameraHeight / 2) / (focalDistance);

                (camera as any)._currentTarget = camTar;
                camera.setPosition(camTar.add(camDirection.scale(Math.abs(focalDistance))));

                camera.minZ = -10000;
            } else {
                camera.radiusToOrthoScale = 1;

                camera._currentTarget = position.subtract(camDirection.scale(Math.abs(focalDistance)));
                camera.setPosition(position);
                camera.minZ = 0.5;
            }

            camera.getProjectionMatrix(true);
            camera.updateCache(true);

            camera.computeViewMatrix(true);
            this.setCameraMode(mode);

            const cameraView: BABYLON.Matrix = camera.getViewMatrix();
            const cameraOrientation = new BABYLON.Vector3(cameraView.m[0], cameraView.m[2], cameraView.m[1]);
            const classicOrientation = new BABYLON.Vector3(Xt.x, Yt.x * -1, Zt.x);

            const cameraUp = new BABYLON.Vector3(cameraView.m[8], cameraView.m[10], cameraView.m[9]);
            const classicUp = new BABYLON.Vector3(Xt.x, Yt.x * -1, Zt.x);
            const upRotation = Math.max(-1, Math.min(1, BABYLON.Vector3.Dot(cameraUp, classicUp)));

            const dotProduct = Math.max(-1, Math.min(1, BABYLON.Vector3.Dot(cameraOrientation, classicOrientation)));

            this.addHeadlights(camera as any);

            (camera as any).zoomFactor = 1;
            (camera as any).panningSpeed = 1;

            this._sceneDirty = true;

            if (this._coordinator == null) {
                this._coordinator = new FrustumMeshCoordinator.FrustumMeshCoordinatorWorker();
                this._coordinator.init(this, this._babScene);
            }

            const position2 = new BABYLON.Vector3(position.x, position.y, position.z);
            camera.getProjectionMatrix(true);
            camera.updateCache(true);

            camera.computeViewMatrix(true);
            (camera as any)._getViewMatrix?.();
            position = position2;

            const newRow = new BABYLON.Vector4(position.x, position.y, position.z, camera.getWorldMatrix().m[15]);
            camera.getWorldMatrix().setRow(3, newRow);

            return;
        }

        // ---------------------------------------------------------------------
        // UniversalCamera branch
        // ---------------------------------------------------------------------
        if (isUniversalCamera(camera)) {
            const u: any = camera;

            // Projection mode
            if (mode === Enumerations.CameraMode.ORTHOGRAPHIC) {
                u.mode = BABYLON.Camera.ORTHOGRAPHIC_CAMERA;

                const halfH = (cameraHeight / 2);
                const aspect = this._babEngine.getRenderingCanvasClientRect
                    ? (this._babEngine.getRenderingCanvasClientRect().width /
                        this._babEngine.getRenderingCanvasClientRect().height)
                    : 1;

                const halfW = halfH * aspect;

                u.orthoTop = halfH;
                u.orthoBottom = -halfH;
                u.orthoRight = halfW;
                u.orthoLeft = -halfW;

                u.minZ = -10000;
            } else {
                u.mode = BABYLON.Camera.PERSPECTIVE_CAMERA;
                u.minZ = 0.5;
            }

            // Position
            if (u.position?.copyFrom) u.position.copyFrom(position);
            else u.position = position.clone();

            const euler = quaternion.toEulerAngles();
            if (u.rotation?.copyFrom) u.rotation.copyFrom(euler);
            else u.rotation = euler;

            u.rotationQuaternion = null;

            const camDirection = Yt;
            const target = position.subtract(camDirection.scale(focalDistance));

            if (typeof u.setTarget === "function") u.setTarget(target);
            else u.target = target.clone();

            // Clear inertia so initialization doesn’t drift
            if (u.inertialMoveForward !== undefined) u.inertialMoveForward = 0;
            if (u.inertialMoveRight !== undefined) u.inertialMoveRight = 0;
            if (u.inertialMoveUp !== undefined) u.inertialMoveUp = 0;
            if (u.inertialAlphaOffset !== undefined) u.inertialAlphaOffset = 0;
            if (u.inertialBetaOffset !== undefined) u.inertialBetaOffset = 0;

            this.addHeadlights(u);

            // Force matrices/cache updates
            u.getProjectionMatrix?.(true);
            u.getViewMatrix?.(true);
            u.computeWorldMatrix?.(true);

            this._sceneDirty = true;

            if (this._coordinator == null) {
                this._coordinator = new FrustumMeshCoordinator.FrustumMeshCoordinatorWorker();
                this._coordinator.init(this, this._babScene);
            }

            try {
                const wm = u.getWorldMatrix?.();
                if (wm?.setRow) {
                    const newRow = new BABYLON.Vector4(position.x, position.y, position.z, wm.m[15]);
                    wm.setRow(3, newRow);
                }
            } catch { }

            return;
        }
    }

    
    public async setCameraProperties(
        position: BABYLON.Vector3,
        quaternion: BABYLON.Quaternion,
        focalDistance: number,
        cameraHeight: number,
        mode: Enumerations.CameraMode,
        animate?: boolean,
        initialproperties?: boolean
    ) {
        const camera = this.scene.activeCamera as AnyCam;
        const c: any = camera;

        focalDistance = Math.max(0.001, Math.abs(focalDistance));

        const cosVal = Math.cos(quaternion.w);
        const sinVal = Math.sin(quaternion.w);

        const Xt = new BABYLON.Vector3(
            cosVal + (quaternion.x * quaternion.x) * (1 - cosVal),
            quaternion.z * sinVal + quaternion.x * quaternion.y * (1 - cosVal),
            -quaternion.y * sinVal + quaternion.x * quaternion.z * (1 - cosVal)
        );

        const Yt = new BABYLON.Vector3(
            quaternion.x * quaternion.y * (1 - cosVal) - quaternion.z * sinVal,
            cosVal + quaternion.y * quaternion.y * (1 - cosVal),
            quaternion.x * sinVal + quaternion.y * quaternion.z * (1 - cosVal)
        );

        const camDirection = Yt;
        const finalTarget = position.subtract(camDirection.scale(focalDistance));
        const finalPos = position.clone();

        const animKind: CameraAnimKind = initialproperties ? "initial" : "normal";

        // ------------------------------------------------------------
        // Branch: ExamineCamera (orbit camera)
        // ------------------------------------------------------------
        if (isExamineCamera(camera as any)) {
            const ex = camera as unknown as ExamineCamera;
            const exAny: any = ex;

            ex.mode = mode;

            if (mode === Enumerations.CameraMode.ORTHOGRAPHIC) {
                const camTar = finalTarget;

                const orthoFocal = (cameraHeight / 2) / 0.51;
                ex.radiusToOrthoScale = (cameraHeight / 2) / orthoFocal;

                exAny._currentTarget = camTar;
                exAny.setPosition(camTar.add(camDirection.scale(focalDistance)));

                ex.minZ = -10000;

                ex.getProjectionMatrix(true);
                ex.updateCache(true);
                ex.computeViewMatrix(true);
                this.setCameraMode(mode);
            } else {
                ex.radiusToOrthoScale = 1;
                ex.minZ = 0.5;

                if (animate) {
                    await this.animateCameraTo(this._babScene, ex as any, {
                        target: finalTarget,
                        position: finalPos,
                        maxMs: 2000,
                        easing: "cubicInOut",
                        targetLead: 0.15,
                        preserveForward: false,
                        kind: animKind,
                    });

                    this.setCameraMode(mode);
                } else {
                    exAny._currentTarget = finalTarget;
                    exAny.setPosition(finalPos);

                    ex.getProjectionMatrix(true);
                    ex.updateCache(true);
                    ex.computeViewMatrix(true);
                    this.setCameraMode(mode);

                    const cameraView: BABYLON.Matrix = ex.getViewMatrix();

                    const cameraOrientation = new BABYLON.Vector3(cameraView.m[0], cameraView.m[2], cameraView.m[1]);
                    const classicOrientation = new BABYLON.Vector3(Xt.x, Yt.x * -1, Xt.z);

                    const cameraUp = new BABYLON.Vector3(cameraView.m[8], cameraView.m[10], cameraView.m[9]);
                    const classicUp = new BABYLON.Vector3(Xt.x, Yt.x * -1, Xt.z);

                    const upRotation = Math.max(-1, Math.min(1, BABYLON.Vector3.Dot(cameraUp, classicUp)));
                    const dotProduct = Math.max(-1, Math.min(1, BABYLON.Vector3.Dot(cameraOrientation, classicOrientation)));

                    this._completeInitialCameraSetup();
                }
            }

            this.addHeadlights(ex);
            ex.zoomFactor = 1;
            ex.panningSpeed = 1;

            this._sceneDirty = true;

            if (this._coordinator == null) {
                this._coordinator = new FrustumMeshCoordinator.FrustumMeshCoordinatorWorker();
                this._coordinator.init(this, this._babScene);
            }

            const position2 = new BABYLON.Vector3(position.x, position.y, position.z);
            ex.getProjectionMatrix(true);
            ex.updateCache(true);
            ex.computeViewMatrix(true);
            (ex as any)._getViewMatrix?.();

            position = position2;

            const newRow = new BABYLON.Vector4(position.x, position.y, position.z, ex.getWorldMatrix().m[15]);
            ex.getWorldMatrix().setRow(3, newRow);

            return;
        }

        // ------------------------------------------------------------
        // Branch: Custom / wrapped UniversalCamera
        // ------------------------------------------------------------
        if (isUniversalCamera(camera as any)) {
            const fly = camera as unknown as UniversalCamera;
            const flyAny: any = fly;

            if (mode === Enumerations.CameraMode.ORTHOGRAPHIC) {
                fly.mode = BABYLON.Camera.ORTHOGRAPHIC_CAMERA;

                const orthoFocal = (cameraHeight / 2) / 0.51;
                const orthoScale = (cameraHeight / 2) / orthoFocal;

                const r = orthoFocal;
                fly.orthoBottom = -r * orthoScale;
                fly.orthoTop = r * orthoScale;
                fly.orthoLeft = -r * orthoScale;
                fly.orthoRight = r * orthoScale;

                fly.minZ = -10000;

                fly.position.copyFrom(finalPos);

                const euler = quaternion.toEulerAngles();
                fly.rotation.copyFrom(euler);
                fly.rotationQuaternion = null;

                const forward = camDirection.scale(-1);
                const tgt = finalPos.add(forward.scale(focalDistance));
                if (typeof flyAny.setTarget === "function") flyAny.setTarget(tgt);
                else flyAny.target = tgt;
            } else {
                fly.mode = BABYLON.Camera.PERSPECTIVE_CAMERA;
                fly.minZ = 0.5;

                if (animate) {
                    const currentTarget =
                        typeof flyAny.getTarget === "function"
                            ? flyAny.getTarget().clone()
                            : flyAny.target instanceof BABYLON.Vector3
                                ? flyAny.target.clone()
                                : fly.position.add(fly.getForwardRay().direction);

                    const currentForwardDir = currentTarget.subtract(fly.position).normalize();
                    const currentDistance = currentTarget.subtract(fly.position).length();

                    await this.animateCameraTo(this._babScene, fly as any, {
                        target: finalTarget,
                        position: finalPos,
                        maxMs: 2000,
                        easing: "cubicInOut",
                        targetLead: 0.15,
                        preserveForward: false,
                        kind: animKind
                    });
                } else {
                    fly.position.copyFrom(finalPos);

                    const euler = quaternion.toEulerAngles();
                    fly.rotation.copyFrom(euler);
                    fly.rotationQuaternion = null;

                    const forward = camDirection.scale(-1);
                    const tgt = finalPos.add(forward.scale(focalDistance));
                    if (typeof flyAny.setTarget === "function") flyAny.setTarget(tgt);
                    else flyAny.target = tgt;

                    this._completeInitialCameraSetup();
                }
            }

            // Reset inertia channels so we don't apply old deltas after teleport / animate
            flyAny.inertialAlphaOffset = 0;
            flyAny.inertialBetaOffset = 0;
            flyAny.inertialMoveForward = 0;
            flyAny.inertialMoveRight = 0;
            flyAny.inertialMoveUp = 0;

            this.addHeadlights(fly as any);

            this._sceneDirty = true;

            if (this._coordinator == null) {
                this._coordinator = new FrustumMeshCoordinator.FrustumMeshCoordinatorWorker();
                this._coordinator.init(this, this._babScene);
            }

            fly.getProjectionMatrix(true);
            fly.getViewMatrix(true);
            fly.computeWorldMatrix();

            const newRow = new BABYLON.Vector4(position.x, position.y, position.z, fly.getWorldMatrix().m[15]);
            fly.getWorldMatrix().setRow(3, newRow);

            return;
        }

        // ------------------------------------------------------------
        // Branch: Fallback Floating UniversalCamera
        // ------------------------------------------------------------
        const fly = camera as unknown as BABYLON.UniversalCamera;
        const flyAny: any = fly;

        if (mode === Enumerations.CameraMode.ORTHOGRAPHIC) {
            fly.mode = BABYLON.Camera.ORTHOGRAPHIC_CAMERA;

            const orthoFocal = (cameraHeight / 2) / 0.51;
            const orthoScale = (cameraHeight / 2) / orthoFocal;

            const r = orthoFocal;
            fly.orthoBottom = -r * orthoScale;
            fly.orthoTop = r * orthoScale;
            fly.orthoLeft = -r * orthoScale;
            fly.orthoRight = r * orthoScale;

            fly.minZ = -10000;

            fly.position.copyFrom(finalPos);

            const euler = quaternion.toEulerAngles();
            fly.rotation.copyFrom(euler);
            fly.rotationQuaternion = null;

            const forward = camDirection.scale(-1);
            fly.setTarget(fly.position.add(forward.scale(focalDistance)));
        } else {
            fly.mode = BABYLON.Camera.PERSPECTIVE_CAMERA;
            fly.minZ = 0.5;

            if (animate) {
                const currentTarget =
                    typeof flyAny.getTarget === "function"
                        ? flyAny.getTarget().clone()
                        : flyAny.target instanceof BABYLON.Vector3
                            ? flyAny.target.clone()
                            : fly.position.add(fly.getForwardRay().direction);

                const currentForwardDir = currentTarget.subtract(fly.position).normalize();
                const currentDistance = currentTarget.subtract(fly.position).length();

                await this.animateCameraTo(this._babScene, fly as any, {
                    target: finalTarget,
                    position: finalPos,
                    maxMs: 2000,
                    easing: "cubicInOut",
                    targetLead: 0.15,
                    preserveForward: false,
                    kind: animKind
                });
            } else {
                fly.position.copyFrom(finalPos);

                const euler = quaternion.toEulerAngles();
                fly.rotation.copyFrom(euler);
                fly.rotationQuaternion = null;

                const forward = camDirection.scale(-1);
                fly.setTarget(fly.position.add(forward.scale(focalDistance)));
            }
        }

        if (typeof flyAny.setControlsSuspended === "function") {
            flyAny.setControlsSuspended(false, true);
        } else {
            if (flyAny.inertialAlphaOffset !== undefined) flyAny.inertialAlphaOffset = 0;
            if (flyAny.inertialBetaOffset !== undefined) flyAny.inertialBetaOffset = 0;
            if (flyAny.inertialMoveForward !== undefined) flyAny.inertialMoveForward = 0;
            if (flyAny.inertialMoveRight !== undefined) flyAny.inertialMoveRight = 0;
            if (flyAny.inertialMoveUp !== undefined) flyAny.inertialMoveUp = 0;
        }

        this.addHeadlights(fly as any);

        this._sceneDirty = true;

        if (this._coordinator == null) {
            this._coordinator = new FrustumMeshCoordinator.FrustumMeshCoordinatorWorker();
            this._coordinator.init(this, this._babScene);
        }

        fly.getProjectionMatrix(true);
        fly.getViewMatrix(true);
        fly.computeWorldMatrix();

        const newRow = new BABYLON.Vector4(position.x, position.y, position.z, fly.getWorldMatrix().m[15]);
        fly.getWorldMatrix().setRow(3, newRow);
    }

    private _completeInitialCameraSetup() {
        this._initialCameraAnimationInProgress = false;
        this._initialCameraAnimationCompleted = true;

        const queued = this._queuedCameraRequest;
        this._queuedCameraRequest = null;

        if (queued) {
            try {
                this.animateCameraTo(queued.scene, queued.cam, {
                    ...queued.opts,
                    kind: "normal"
                }).then(queued.resolve, queued.reject);
            } catch (err) {
                queued.reject(err);
            }
        }
    }

    onRegionNodesUpdated(sceneManager: SceneManager, visibleNodes: BABYLON.SmartArray<FrustumStructures.TileNode>, hiddenNodes: BABYLON.SmartArray<FrustumStructures.TileNode>) {


        if (sceneManager._regionCoordinators.length > 0) {
            var nodes = visibleNodes as any;
            for (var x = 0; x < nodes.length; x++) {

                (sceneManager._displayManager as any)?.setRegionMesh(nodes[x]);
            }
        }

        if (sceneManager._dataRegionRequestCallback != null) {

            sceneManager._dataRegionRequestCallback(sceneManager._displayManager, visibleNodes, hiddenNodes);
        }
    }
    onNodesUpdated(sceneManager: SceneManager, visibleNodes: BABYLON.SmartArray<FrustumStructures.TreeNode>, hiddenNodes: BABYLON.SmartArray<FrustumStructures.TreeNode>) {

        if (sceneManager._dataRequestCallback != null) {

            sceneManager._dataRequestCallback(sceneManager._displayManager, visibleNodes, hiddenNodes);
        }
    }

    setCameraFocalDistance(distance: number) {

        var cam = this.scene.activeCamera as AnyCam;

        if (isExamineCamera(cam)) {
            cam.radius = distance;
        }
    }

    setBackgroundScene(sceneType: Enumerations.BackgroundSceneType, textureUrl: string) {

        var files: string[] = [];

        if (this._skyBox) {
            this._skyBox.dispose();
        }
        if (this._skyBoxTexture) {
            this._skyBoxTexture.dispose();
        }

        switch (sceneType) {
            case Enumerations.BackgroundSceneType.DUSK:
                files = Textures.Dusk;
                break;
            case Enumerations.BackgroundSceneType.DESERT:
                files = Textures.Desert;
                break;
            case Enumerations.BackgroundSceneType.DAYLIGHT:
                files = Textures.Daylight;
                break;
            case Enumerations.BackgroundSceneType.NIGHTTIME:
                files = Textures.Nighttime;
                break;
            case Enumerations.BackgroundSceneType.DAWN:
                files = Textures.Dawn;
                break;
            case Enumerations.BackgroundSceneType.OFFSHORE:
            default:
                files = Textures.Offshore;
                break;
        }

        // Add Url path to file paths
        var images: string[] = [];
        for (var i = 0; i < files.length; i++) {
            images[i] = (files[i].indexOf('data:') === 0 ? "" : textureUrl) + files[i];
        }

        this._skyBox = BABYLON.Mesh.CreateBox("skyBox", 10000.0, this._babScene);
        this._skyBoxTexture = new BABYLON.StandardMaterial("skyBox", this._babScene);
        this._skyBoxTexture.backFaceCulling = false;
        this._skyBoxTexture.reflectionTexture = BABYLON.CubeTexture.CreateFromImages(images, this._babScene);
        this._skyBoxTexture.reflectionTexture.coordinatesMode = BABYLON.Texture.SKYBOX_MODE;
        this._skyBoxTexture.diffuseColor = new BABYLON.Color3(0, 0, 0);
        this._skyBoxTexture.specularColor = new BABYLON.Color3(0, 0, 0);
        this._skyBox.infiniteDistance = true;
        this._skyBox.renderingGroupId = 0;
        this._skyBox.isPickable = false;
        this._skyBox.material = this._skyBoxTexture;
    }
    setBackgroundColor(color: BABYLON.Color4) {
        this._babScene.clearColor = color;
    }

    setClickMode(mode: Enumerations.ClickMode) {
        this._clickMode = mode;
    }

    setSelectionMode(mode: Enumerations.SelectMode) {
        this._selectMode = mode;
    }

    private _getPoseFromCamera(cam: BABYLON.Camera): CameraPose {
        const pos = cam.position.clone();

        // Prefer Babylon APIs when present
        const anyCam: any = cam;

        let tgt: BABYLON.Vector3;

        if (typeof anyCam.getTarget === "function") {
            tgt = anyCam.getTarget().clone();
        } else if (anyCam.target instanceof BABYLON.Vector3) {
            tgt = anyCam.target.clone();
        } else if (anyCam._currentTarget instanceof BABYLON.Vector3) {
            tgt = anyCam._currentTarget.clone();
        } else {
            // fallback: forward ray
            tgt = pos.add(cam.getForwardRay().direction);
        }

        const focalDistance = pos.subtract(tgt).length();

        // Quaternion from view matrix (reliable across both)
        const view = cam.getViewMatrix();
        const q = BABYLON.Quaternion.FromRotationMatrix(view);

        // Ortho "height" if available
        let cameraHeight: number | undefined;
        if (cam.mode === BABYLON.Camera.ORTHOGRAPHIC_CAMERA) {
            // UniversalCamera uses orthoTop/bottom etc
            if (typeof cam.orthoTop === "number" && typeof cam.orthoBottom === "number") {
                cameraHeight = Math.abs(cam.orthoTop - cam.orthoBottom);
            } else if (typeof anyCam.radiusToOrthoScale === "number") {
                cameraHeight = anyCam.radiusToOrthoScale * focalDistance * 2;
            }
        }

        return {
            position: pos,
            target: tgt,
            quaternion: q,
            mode: cam.mode,
            focalDistance,
            cameraHeight,
        };
    }


    private _applyPoseToExamine(ex: ExamineCamera, pose: CameraPose) {
        const exAny: any = ex;
        ex.mode = pose.mode;

        // Position/target
        exAny._currentTarget = pose.target.clone();
        ex.setPosition(pose.position.clone());

        // Ortho sizing
        if (pose.mode === BABYLON.Camera.ORTHOGRAPHIC_CAMERA) {
            const fd = Math.max(0.001, pose.focalDistance);
            const h = pose.cameraHeight ?? (fd * 2); // fallback

            ex.radiusToOrthoScale = (h / 2) / fd;
            ex.minZ = -10000;
        } else {
            ex.radiusToOrthoScale = 1;
            ex.minZ = 0.5;
        }

        if (typeof exAny.updateMode === "function") {
            exAny.updateMode(ex.mode);
        }

        ex.getProjectionMatrix(true);
        ex.updateCache(true);
        ex.computeViewMatrix(true);

        this.addHeadlights(ex);
    }


    private _applyPoseToFly(fly: UniversalCamera, pose: CameraPose) {
        // Mode
        fly.mode = pose.mode;

        // Position
        fly.position.copyFrom(pose.position);

        const euler = pose.quaternion.toEulerAngles();
        fly.rotation.copyFrom(euler);
        fly.rotationQuaternion = null;

        // Target
        fly.setTarget(pose.target);

        // Ortho sizing
        if (pose.mode === BABYLON.Camera.ORTHOGRAPHIC_CAMERA) {
            // If we have cameraHeight, size ortho window around it
            const h = pose.cameraHeight ?? 10;
            const halfH = h / 2;
            fly.orthoTop = halfH;
            fly.orthoBottom = -halfH;

            fly.orthoLeft = -halfH;
            fly.orthoRight = halfH;

            fly.minZ = -10000;
        } else {
            fly.minZ = 0.5;
        }

        // reset fly inertia so it doesn’t drift after switching
        fly.inertialAlphaOffset = 0;
        fly.inertialBetaOffset = 0;
        fly.inertialMoveForward = 0;
        fly.inertialMoveRight = 0;
        fly.inertialMoveUp = 0;

        this.addHeadlights(fly);

        fly.getProjectionMatrix(true);
        fly.getViewMatrix(true);
        fly.computeWorldMatrix();
    }

    setCrosshairEnabled(enabled: boolean) {
        if (!this._crosshair) {
            this._createCrosshair();
        }

        this._crosshair.isVisible = enabled;
    }

    setCameraInvertY(invert: boolean) {
        if (!this._examineCamera) {
            this._examineCamera = new ExamineCamera(
                "ExamineCamera",
                this._eventHandler,
                1, 0.8, 10,
                new BABYLON.Vector3(1, 0, 0),
                this._babScene
            );
            this._babScene.addCamera(this._examineCamera);
        }
        if (!this._universalCamera) {
            this._universalCamera = new UniversalCamera(
                "Universal Camera",
                this._eventHandler,
                new BABYLON.Vector3(1, 0, 0),
                this._babScene
            );
            this._babScene.addCamera(this._universalCamera);
        }

        this._examineCamera.setInvertY(invert);
        this._universalCamera.setInvertY(invert);
    }

    setCameraRotationSensitivity(sensitivity: number) {

        if (!this._examineCamera) {
            this._examineCamera = new ExamineCamera(
                "ExamineCamera",
                this._eventHandler,
                1, 0.8, 10,
                new BABYLON.Vector3(1, 0, 0),
                this._babScene
            );
            this._babScene.addCamera(this._examineCamera);
        }
        if (!this._universalCamera) {
            this._universalCamera = new UniversalCamera(
                "Universal Camera",
                this._eventHandler,
                new BABYLON.Vector3(1, 0, 0),
                this._babScene
            );
            this._babScene.addCamera(this._universalCamera);
        }

        this._examineCamera.setRotationSensitivity(sensitivity);
        this._universalCamera.setRotationSensitivity(sensitivity);

    }

    setCameraVelocitySensitivity(sensitivity: number) {
        if (!this._examineCamera) {
            this._examineCamera = new ExamineCamera(
                "ExamineCamera",
                this._eventHandler,
                1, 0.8, 10,
                new BABYLON.Vector3(1, 0, 0),
                this._babScene
            );
            this._babScene.addCamera(this._examineCamera);
        }
        if (!this._universalCamera) {
            this._universalCamera = new UniversalCamera(
                "Universal Camera",
                this._eventHandler,
                new BABYLON.Vector3(1, 0, 0),
                this._babScene
            );
            this._babScene.addCamera(this._universalCamera);
        }

        this._examineCamera.setVelocitySensitivity(sensitivity);
        this._universalCamera.setVelocitySensitivity(sensitivity);
    }

    focusCameraInput() {
        const activeCamera = this.scene.activeCamera as AnyCam & { focusInput?: () => void };

        if (activeCamera && typeof activeCamera.focusInput === "function") {
            activeCamera.focusInput();
            return;
        }

        this._babEngine.getRenderingCanvas()?.focus();
    }

    setCameraNavigationMode(mode: Enumerations.CameraNavigationMode) {
        const canvas = this._babEngine.getRenderingCanvas() as any;
        const current = this.scene.activeCamera as AnyCam;
        if (!current) return;

        const pose = this._getPoseFromCamera(current);

        // ---------------------------
        // Examine / Pan => ExamineCamera
        // ---------------------------
        if (
            mode === Enumerations.CameraNavigationMode.Examine ||
            mode === Enumerations.CameraNavigationMode.Pan
        ) {
            // Switch to Examine if needed
            if (!isExamineCamera(current)) {
                // detach old controls (best-effort)
                (current as any).detachControl?.(canvas);

                // create examine if needed
                if (!this._examineCamera) {
                    this._examineCamera = new ExamineCamera(
                        "ExamineCamera",
                        this._eventHandler,
                        1, 0.8, 10,
                        new BABYLON.Vector3(1, 0, 0),
                        this._babScene
                    );
                    this._babScene.addCamera(this._examineCamera);
                }

                this._applyPoseToExamine(this._examineCamera, pose);
                this._babScene.switchActiveCamera(this._examineCamera);
                this._examineCamera.attachControl(canvas, true, false);
            }

            // Now active is examine
            const ex = this.scene.activeCamera as any;
            ex.cameraNavigationMode = mode; // Examine or Pan
            this._sceneDirty = true;

            this.applyEdgeShader(ex);
            return;
        }

        // ---------------------------
        // Free/Fly => Universal camera controller
        // ---------------------------
        if (
            mode === Enumerations.CameraNavigationMode.Universal ||
            mode === Enumerations.CameraNavigationMode.Fly
        ) {
            if (!isUniversalCamera(current)) {

                (current as any).detachControl?.(canvas);

                if (!this._universalCamera) {
                    this._universalCamera = new UniversalCamera(
                        "Universal Camera",
                        this._eventHandler,
                        pose.position.clone(),
                        this._babScene
                    );
                    this._babScene.addCamera(this._universalCamera);
                }

                this._applyPoseToFly(this._universalCamera, pose);
                this._babScene.switchActiveCamera(this._universalCamera);
                this._universalCamera.attachControl(canvas, true, false);
            }

            const fly = this.scene.activeCamera as UniversalCamera;
            fly.setNavigationMode(mode);
            this._sceneDirty = true;
            this.applyEdgeShader(fly);
            return;
        }
    }


    setCameraMode(mode: Enumerations.CameraMode) {
        var cam = this.scene.activeCamera as any;

        if (isExamineCamera(cam)) {

            if (cam.mode == BABYLON.Camera.ORTHOGRAPHIC_CAMERA) {

                if (mode == Enumerations.CameraMode.ORTHOGRAPHIC)
                    return;

                if (this._shaderEnabled) {
                    this.applyEdgeShader(cam);
                }

                cam.updateMode(Enumerations.CameraMode.PERSPECTIVE);

                if (this._skyBox) {
                    this._skyBox.setEnabled(true);
                }

                cam.minZ = .5;
            }
            else {

                if (mode == Enumerations.CameraMode.PERSPECTIVE)
                    return;

                if (this._shaderEnabled) {
                    cam.detachPostProcess(this._edgeProcess);
                }

                cam.updateMode(Enumerations.CameraMode.ORTHOGRAPHIC);

                if (this._skyBox) {
                    this._skyBox.setEnabled(false);
                }

                cam.minZ = -10000;
            }
        }
    }
    setPickMode(mode: Enumerations.PickMode) {

        this._pickMode = mode;
    }

    getSceneExtents() {

        var min = new BABYLON.Vector3(this._sceneContentExtents.minimum.x * 1000, this._sceneContentExtents.minimum.y * 1000, this._sceneContentExtents.minimum.z * 1000);
        var max = new BABYLON.Vector3(this._sceneContentExtents.maximum.x * 1000, this._sceneContentExtents.maximum.y * 1000, this._sceneContentExtents.maximum.z * 1000);
        var retVal = {
            Minimum: min, Maximum: max
        };

        return retVal;
    }

    viewAll() {

        this.viewBoundingBox(this._sceneContentExtents);
    }

    resetCoordinators() {

        if (this._coordinator) {
            try { this._coordinator.destroy(); } catch { }
            this._coordinator = null;
        }

        if (this._regionCoordinators && this._regionCoordinators.length) {
            
            for (const coord of this._regionCoordinators) {
                try { coord.destroy(); } catch { }
            }
            this._regionCoordinators.length = 0;
        }

        this._dataRequestCallback = null;
        this._dataRegionRequestCallback = null;
    }

    setCoordinatorOctree(callback: (displayManager: Interfaces.Interfaces.IDisplayManager, visibleNodes: BABYLON.SmartArray<FrustumStructures.TreeNode>, hiddenNodes: BABYLON.SmartArray<FrustumStructures.TreeNode>) => void, octrees: FrustumMeshWorker.OctreeBlock<FrustumStructures.TreeNode>[]) {

        if (this._coordinator) {
            try { this._coordinator.destroy(); } catch { }
            this._coordinator = null;
        }

        this._dataRequestCallback = callback;

        if (this._coordinator == null) {
            this._coordinator = new FrustumMeshCoordinator.FrustumMeshCoordinatorWorker();
            this._coordinator.init(this, this._babScene);
        }

        for (var x = 0; x < octrees.length; x++) {
            this._coordinator.OnOctreeBlockAdded(octrees[x]);
        }
    }

    addCoordinatorOctreeBlocks(callback: (displayManager: Interfaces.Interfaces.IDisplayManager, visibleNodes: BABYLON.SmartArray<FrustumStructures.TreeNode>, hiddenNodes: BABYLON.SmartArray<FrustumStructures.TreeNode>) => void, octrees: FrustumMeshWorker.OctreeBlock<FrustumStructures.TreeNode>[]) {
        if (!octrees?.length) return;

        this._dataRequestCallback = callback;
        if (this._coordinator == null) {
            this._coordinator = new FrustumMeshCoordinator.FrustumMeshCoordinatorWorker();
            this._coordinator.init(this, this._babScene);
        }

        for (const octree of octrees) {
            this._coordinator.OnOctreeBlockAdded(octree);
        }
    }

    setRegionCoordinatorOctree(callback: (displayManager: Interfaces.Interfaces.IDisplayManager, visibleNodes: BABYLON.SmartArray<FrustumStructures.TileNode>, hiddenNodes: BABYLON.SmartArray<FrustumStructures.TileNode>) => void, octrees: FrustumRegionWorker.OctreeBlock<FrustumStructures.TileNode>[]) {


        if (this._regionCoordinators && this._regionCoordinators.length) {
            for (const coord of this._regionCoordinators) {
                try { coord.destroy(); } catch { }
            }
            this._regionCoordinators.length = 0;
        }

        this._dataRegionRequestCallback = callback;
        var coordinator = new FrustumRegionCoordinator.FrustumRegionCoordinatorWorker();
        coordinator.init(this, this._babScene);

        for (var x = 0; x < octrees.length; x++) {
            coordinator.OnOctreeBlockAdded(octrees[x]);
        }

        this._regionCoordinators.push(coordinator);
    }

    _pickPointCloud(x: number, y: number) {

        const cam = this.scene.activeCamera as BABYLON.Camera;
        const ray = this._babScene.createPickingRay(x, y, null, cam);
        const pointClouds = (this._meshManager as any).getPointClouds() as BABYLON.Mesh[];

        type Hit = { point: BABYLON.Vector3; mesh: BABYLON.Mesh; dist2: number };
        const hits: Hit[] = [];

        const passesClipPlanes = (pointWS: BABYLON.Vector3): boolean => {
            const s: any = this._babScene;
            const planes: BABYLON.Plane[] = [
                s.clipPlane, s.clipPlane2, s.clipPlane3,
                s.clipPlane4, s.clipPlane5, s.clipPlane6
            ].filter(Boolean);
            for (const p of planes) {
                // Babylon clips when signedDistanceTo(point) < 0
                if (p.signedDistanceTo(pointWS) > 0) return false;
            }
            return true;
        };

        for (let i = 0; i < pointClouds.length; i++) {
            const mesh = pointClouds[i];
            if (!mesh) continue;

            var pickTolerance = mesh.metadata?.spacingWorld ? .8 * mesh.metadata.spacingWorld : .02;
            const bi = mesh.getBoundingInfo();
            if (!ray.intersectsBoxMinMax(bi.minimum, bi.maximum)) continue;

            const positions = mesh.getVerticesData(BABYLON.VertexBuffer.PositionKind);
            if (!positions) continue;

            for (let p = 0; p < positions.length; p += 3) {
                const point = new BABYLON.Vector3(positions[p], positions[p + 1], positions[p + 2]);
                if (this.doesRayIntersectPoint(ray, point, pickTolerance)) {

                    if (passesClipPlanes(point)) {
                        const dist2 = BABYLON.Vector3.DistanceSquared(cam.position, point);
                        hits.push({ point, mesh, dist2 });
                    }
                }
            }
        }

        if (hits.length === 0) return null;

        hits.sort((a, b) => a.dist2 - b.dist2);
        const hit = hits[0];

        return hit.point;
    }

    // This is doing a babylon scene pick but also filters out any hit results that lie outside the scene clip. This should
    // be used in most typical picking cases.
    pickClosestVisible(
        x: number,
        y: number,
        opts?: {
            camera?: BABYLON.Camera;
            predicate?: (mesh: BABYLON.AbstractMesh) => boolean;
            fastCheck?: boolean;
            rejectBackfaces?: boolean; // default: false
        }
    ): BABYLON.PickingInfo {
        const scene = this._babScene;
        const camera = opts?.camera ?? scene.activeCamera!;
        const predicate = opts?.predicate;
        const fastCheck = opts?.fastCheck ?? false;
        const rejectBackfaces = opts?.rejectBackfaces ?? false;

        // Build a ray once for backface checks
        const ray = scene.createPickingRay(x, y, null, camera);
        const cylinderPick = this._meshManager.pickCylinderInstance(ray, predicate);

        // Get all hits (Babylon returns closest-first, but we’ll defensively sort)
        
        const triPred = () => {
            //***IMPORTANT ***/
            // This has been disabled because GLB assemblies have right handed face ordering which means
            // this predicate rejects faces that DO face our camera. We cannot get to the mesh's transformation
            // and undo the incorrect winding (triangle prediate is mesh agnostic). So, without this predicate
            // there is the case that if the pick hits a backface first (which gets rejected down below), but the same
            // mesh could have been picked past that backface, then it won't pick that mesh. The multipick only gets ONE
            // pick result PER mesh. Not every point along that mesh that was hit. Just ask steve about this, I'm sure
            // he'll remember all this in 6 months. -Steve
            //return BABYLON.Vector3.Dot(r.direction, n) >= 1e-5; // keep front-facing only

            return true;
        };

        const scenePredicate = (mesh: BABYLON.AbstractMesh) => {
            if (predicate && !predicate(mesh)) {
                return false;
            }

            if (!mesh.isEnabled() || !mesh.isVisible || !mesh.isPickable) {
                return false;
            }

            const material = mesh.material as any;
            if (material?.name === "HidMat" || material?.id === "HidMat") {
                return false;
            }

            const isPointCloudMesh =
                material?.pointsCloud
                || mesh.metadata?.spacingWorld != null
                || mesh.metadata?.voxelMM != null
                || mesh.name?.startsWith("PointCloud_")
                || mesh.id?.startsWith("PointCloud_");

            if (isPointCloudMesh) {
                return false;
            }
            
            return !((mesh as any).hasThinInstances && !(mesh as any).thinInstanceEnablePicking);
        };

        const hits = (scene.multiPick(x, y, scenePredicate, camera, triPred) || []).sort(
            (a, b) => (a?.distance ?? Number.POSITIVE_INFINITY) - (b?.distance ?? Number.POSITIVE_INFINITY)
        );

        // Helper: does a world-space point pass all active clip planes?
        const passesClipPlanes = (pointWS: BABYLON.Vector3): boolean => {
            const s: any = scene;
            const planes: BABYLON.Plane[] = [
                s.clipPlane, s.clipPlane2, s.clipPlane3,
                s.clipPlane4, s.clipPlane5, s.clipPlane6
            ].filter(Boolean);
            for (const p of planes) {
                // Babylon clips when signedDistanceTo(point) < 0
                if (p.signedDistanceTo(pointWS) > 0) return false;
            }
            return true;
        };

        let bestSceneHit: BABYLON.PickingInfo | null = null;

        for (const h of hits) {
            if (!h?.hit) continue;

            const pt = h.pickedPoint;
            if (!pt || !passesClipPlanes(pt)) continue;

            const thinInstanceHit = h.thinInstanceIndex != null && h.thinInstanceIndex >= 0;
            const pickedObjectId = thinInstanceHit
                ? this._meshManager.getObjectIdByThinInstance(h.pickedMesh.id, h.thinInstanceIndex)
                : this._meshManager.getObjectIdByFace(h.pickedMesh.id, h.faceId);

            if (this._meshManager.isObjectHiddenByMaterial(h.pickedMesh.id, pickedObjectId)) {
                continue;
            }

            if (rejectBackfaces && !thinInstanceHit) {

                const n = h.getNormal(true, true); // world-space, prefer vertex normals
                if (n) {
                    // If ray and normal point roughly the same way, it's a backface (reject)
                    if (BABYLON.Vector3.Dot(ray.direction, n) > 0) {

                        continue;
                    }
                }
            }

            bestSceneHit = h;
            break;
        }

        if (cylinderPick && passesClipPlanes(cylinderPick.pickedPoint)) {
            if (!bestSceneHit || cylinderPick.distance < bestSceneHit.distance) {
                const cylinderHit = new BABYLON.PickingInfo();
                cylinderHit.hit = true;
                cylinderHit.distance = cylinderPick.distance;
                cylinderHit.pickedPoint = cylinderPick.pickedPoint;
                cylinderHit.pickedMesh = cylinderPick.pickedMesh;
                cylinderHit.bu = 0;
                cylinderHit.bv = 0;
                cylinderHit.faceId = -1;
                cylinderHit.subMeshId = -1;

                (cylinderHit as any).__objectId = cylinderPick.objectId;
                return cylinderHit;
            }
        }

        if (bestSceneHit) {
            return bestSceneHit;
        }

        // Always return a PickingInfo, even when nothing visible was found
        const empty = new BABYLON.PickingInfo();
        empty.hit = false;
        empty.distance = 0;
        empty.pickedPoint = null as any;
        empty.pickedMesh = null as any;
        empty.bu = 0;
        empty.bv = 0;
        empty.faceId = -1;
        return empty;
    }

    doesRayIntersectPoint(ray: BABYLON.Ray, point: BABYLON.Vector3, tolerance: number) {
        // Calculate the vector from the ray origin to the point
        var originToPoint = point.subtract(ray.origin);

        // Project this vector onto the ray direction
        var rayDirection = ray.direction.normalize();
        var distanceAlongRay = BABYLON.Vector3.Dot(originToPoint, rayDirection);

        // Find the closest point on the ray to the point
        var closestPoint;
        if (distanceAlongRay < 0) {
            // Point is behind the ray's origin
            closestPoint = ray.origin;
        } else {
            closestPoint = ray.origin.add(rayDirection.scale(distanceAlongRay));
        }

        // Check if the distance between the closest point and the point is within the tolerance
        var distance = closestPoint.subtract(point).length();

        return distance <= tolerance;
    }

    async view2DBox(xMin: number, yMin: number, xMax: number, yMax: number) {

        var canvasRect = this._babEngine.getRenderingCanvasClientRect();

        // Treat camera as CamLike for the tween helpers
        var cam = this.scene.activeCamera as unknown as CamLike;
        var camDirection = (cam as any).target.subtract((cam as any).position).normalize();

        // Create the frustum of the 2 D box projected into 3d space.
        var ray1 = this._babScene.createPickingRay(xMin, yMin, null, cam);
        var ray2 = this._babScene.createPickingRay(xMin, yMax, null, cam);
        var ray3 = this._babScene.createPickingRay(xMax, yMin, null, cam);
        var ray4 = this._babScene.createPickingRay(xMax, yMax, null, cam);
        var ray5 = this._babScene.createPickingRay((xMax + xMin) / 2, (yMax + yMin) / 2, null, cam);

        var p5: BABYLON.Vector3 = ray1.origin.add(ray1.direction.scale(Math.abs(cam.maxZ - cam.minZ)));
        var p6: BABYLON.Vector3 = ray2.origin.add(ray2.direction.scale(Math.abs(cam.maxZ - cam.minZ)));
        var p7: BABYLON.Vector3 = ray4.origin.add(ray4.direction.scale(Math.abs(cam.maxZ - cam.minZ)));

        var plane1 = BABYLON.Plane.FromPositionAndNormal(ray1.origin, BABYLON.Vector3.Cross(ray1.direction, ray2.direction));
        var plane2 = BABYLON.Plane.FromPositionAndNormal(ray1.origin, BABYLON.Vector3.Cross(ray3.direction, ray1.direction));
        var plane3 = BABYLON.Plane.FromPositionAndNormal(ray3.origin, BABYLON.Vector3.Cross(ray4.direction, ray3.direction));
        var plane4 = BABYLON.Plane.FromPositionAndNormal(ray4.origin, BABYLON.Vector3.Cross(ray2.direction, ray4.direction));
        var plane5 = BABYLON.Plane.FromPositionAndNormal(ray5.origin, ray5.direction);
        var plane6 = BABYLON.Plane.FromPositionAndNormal(ray5.origin.add(ray5.direction.scale(Math.abs(cam.maxZ - cam.minZ))), ray5.direction.scale(-1));

        var planes: BABYLON.Plane[] = [];
        planes.push(plane5);
        planes.push(plane6);
        planes.push(plane1);
        planes.push(plane3);
        planes.push(plane2);
        planes.push(plane4);

        // Zoom ratio used based on window size, and size of 2d box.
        var zoomRatio = 0;
        var scale = this._babEngine.getHardwareScalingLevel();

        if ((xMax - xMin) > (yMax - yMin)) {
            var width = window.innerWidth;
            zoomRatio = (width / (xMax - xMin));
            zoomRatio *= scale;
        }
        else {
            var height = window.innerHeight;
            zoomRatio = height / ((yMax - yMin));
            zoomRatio *= scale;
        }

        if ((cam as any).mode == BABYLON.Camera.PERSPECTIVE_CAMERA) {

            var intersectedDistance = this._meshManager.getIntersectedDistance(planes, (cam as any).position);
            if (intersectedDistance < 0) {
                intersectedDistance = 0;
            }

            this._meshManager.setPickableMeshes(true);
            var pointerMinX = xMin;
            var pointerMinY = yMin;
            var pointerMaxX = xMax;
            var pointerMaxY = yMax;

            var pick1 = this.pickClosestVisible(pointerMinX, pointerMinY);
            var pick2 = this.pickClosestVisible(pointerMinX, pointerMaxY);
            var pick3 = this.pickClosestVisible(pointerMaxX, pointerMinY);
            var pick4 = this.pickClosestVisible(pointerMaxX, pointerMaxY);
            var pick5 = this.pickClosestVisible((pointerMaxX + pointerMinX) / 2, (pointerMaxY + pointerMinY) / 2);

            var cornerDistance = Math.min(
                pick1.hit ? pick1.distance : 1000000,
                pick2.hit ? pick2.distance : 1000000,
                pick3.hit ? pick3.distance : 1000000,
                pick4.hit ? pick4.distance : 1000000,
                pick5.hit ? pick5.distance : 1000000
            );

            if (cornerDistance == 1000000) {
                // point cloud fallbacks (unchanged)
                var pointPick1 = this._pickPointCloud(pointerMinX, pointerMinY);
                var pointPick2 = this._pickPointCloud(pointerMinX, pointerMaxY);
                var pointPick3 = this._pickPointCloud(pointerMaxX, pointerMinY);
                var pointPick4 = this._pickPointCloud(pointerMaxX, pointerMaxY);
                var pointPick5 = this._pickPointCloud((pointerMaxX + pointerMinX) / 2, (pointerMaxY + pointerMinY) / 2);

                if (pointPick1 != null) cornerDistance = (cam as any).position.subtract(pointPick1).length();
                if (pointPick2 != null) cornerDistance = (cam as any).position.subtract(pointPick2).length();
                if (pointPick3 != null) cornerDistance = (cam as any).position.subtract(pointPick3).length();
                if (pointPick4 != null) cornerDistance = (cam as any).position.subtract(pointPick4).length();
                if (pointPick5 != null) cornerDistance = (cam as any).position.subtract(pointPick5).length();
            }
            this._meshManager.setPickableMeshes(false);

            if (intersectedDistance == 0) {
                intersectedDistance = cornerDistance;
            } else if (cornerDistance > 0) {
                intersectedDistance = Math.min(intersectedDistance, cornerDistance);
            }

            if (intersectedDistance > 0 && intersectedDistance != 1000000) {
                const newRadius = intersectedDistance;

                const finalRay = this._babScene.createPickingRay(
                    (xMax + xMin) / 2, (yMax + yMin) / 2, null, cam as any
                );

                const finalTarget = finalRay.origin.add(finalRay.direction.normalize().scale(newRadius));

                const distanceToCamPlane = BABYLON.Plane.SignedDistanceToPlaneFromPositionAndNormal(
                    (cam as any).position,
                    camDirection,
                    finalTarget
                );

                const lateralAlignedPos = finalTarget.subtract(camDirection.scale(distanceToCamPlane));
                const orientationChangeDistance = (cam as any).position.subtract(lateralAlignedPos).length();

                const dollyDistance = Math.sqrt(newRadius * newRadius - orientationChangeDistance * orientationChangeDistance);
                const zDiff = dollyDistance * (1 - (1 / zoomRatio));

                const finalCamPosition = finalTarget.subtract(
                    camDirection.scale(distanceToCamPlane - zDiff)
                );

                if (this._clickMode == Enumerations.ClickMode.REGION) {
                    this._drawCanvas2dContext.clearRect(0, 0, this._drawCanvas2dContext.canvas.width, this._drawCanvas2dContext.canvas.height);
                }

                // Animate directly to the post-inertia pose; NO dollyDecrement afterwards
                await this.animateCameraTo(this._babScene, cam as CamLike, {
                    target: finalTarget,
                    position: finalCamPosition,
                    maxMs: 3500,
                    easing: "quintOut",
                    targetLead: 0.0,
                    preserveForward: false,   // inferred lock since final forward ≈ initial
                });
            }
        }
        else {

            // Ortho disabled for view/animations.
            /*
            cam.radius /= (zoomRatio);

            var finalRay = this._babScene.createPickingRay((xMax + xMin) / 2, (yMax + yMin) / 2, null, cam);
            var finalTarget = finalRay.origin.add(finalRay.direction.normalize().scale(cam.radius + Math.abs(cam.minZ)));

            var camPosition = cam.position;

            cam._currentTarget = (finalTarget);
            cam.setPosition(finalTarget.subtract(finalRay.direction.normalize().scale(cam.radius)));

            cam.getProjectionMatrix(true)
            cam.updateCache(true);
            cam.computeViewMatrix(true);

            // Called to simply force an UpdateViewport.
            cam.updateMode(BABYLON.Camera.ORTHOGRAPHIC_CAMERA);
            */
        }

        this._babScene.render();
        (cam as any).computeViewMatrix();

        if (this._clickMode == Enumerations.ClickMode.REGION) {
            this._drawCanvas2dContext.clearRect(0, 0, this._drawCanvas2dContext.canvas.width, this._drawCanvas2dContext.canvas.height);
        }

        this._sceneDirty = true;
        this._eventHandler.fireEvent(Enumerations.EventType.CAMERABOXZOOMED);
    }



    dollyVelocity(metersPerSecond: number) {
        var cam = this.scene.activeCamera as any;

        if (isExamineCamera(cam)) {
            cam.setRadiusVelocity(metersPerSecond);
        }
        else if (isUniversalCamera(cam)) {
            cam.setRadiusVelocity(metersPerSecond);
        }
    }


    dollyIncrement(stepSize: number = 1, useSceneTaper: boolean = false) {
        const cam = this.scene.activeCamera as AnyCam;

        if (isExamineCamera(cam)) {
            const taper = useSceneTaper ? (cam._sceneExtentZoomFactor ?? 1) : 1;

            cam.dollyIncrement(stepSize * taper);
            return;
        }

        if (isUniversalCamera(cam)) {
            const taper = useSceneTaper ? (cam._sceneExtentZoomFactor ?? 1) : 1;

            cam.dollyIncrement(stepSize * taper);
            return;
        }
    }


    dollyDecrement(stepSize: number = -1, useSceneTaper: boolean = true) {
        this.dollyIncrement(stepSize, useSceneTaper);
    }

    changeCameraFocalDistance(stepSize: number) {
        var cam = this.scene.activeCamera as any;

        if (isExamineCamera(cam)) {
            cam.flyZoomFactor = stepSize;
        }
    }
    shouldExportNode(node: BABYLON.TransformNode): boolean {
        return node !== this._skyBox;
    }

    generateObjExportString(mesh: BABYLON.Mesh[], materials: boolean, matlibname: string, globalposition: boolean): string {
        var output = [];
        var v = 1;
        if (materials) {
            if (!matlibname) {
                matlibname = 'mat';
            }
            output.push("mtllib " + matlibname + ".mtl");
        }
        for (var j = 0; j < mesh.length; j++) {
            output.push("g object" + j);
            output.push("o object_" + j);
            //Uses the position of the item in the scene, to the file (this back to normal in the end)
            var lastMatrix = null;

            //TODO: submeshes (groups)
            //TODO: smoothing groups (s 1, s off);
            if (materials) {
                var mat = mesh[j].material;
                if (mat) {
                    output.push("usemtl " + mat.id);
                }
            }
            var g = mesh[j].geometry;
            if (!g) {
                continue;
            }
            var trunkVerts = g.getVerticesData('position');
            var trunkNormals = g.getVerticesData('normal');
            var trunkUV = g.getVerticesData('uv');
            var trunkFaces = g.getIndices();
            var curV = 0;
            if (!trunkVerts || !trunkFaces) {
                continue;
            }
            for (var i = 0; i < trunkVerts.length; i += 3) {
                // Babylon.js default is left handed, while OBJ default is right handed
                // Need to invert Z vertices unless Babylon is set to use a right handed system
                if (mesh[0].getScene().useRightHandedSystem) {
                    output.push("v " + trunkVerts[i] + " " + trunkVerts[i + 1] + " " + trunkVerts[i + 2]);
                } else {
                    output.push("v " + trunkVerts[i] + " " + trunkVerts[i + 1] + " " + -trunkVerts[i + 2]);
                }
                curV++;
            }
            if (trunkNormals != null) {
                for (i = 0; i < trunkNormals.length; i += 3) {
                    if (mesh[0].getScene().useRightHandedSystem) {
                        output.push("vn " + trunkNormals[i] + " " + trunkNormals[i + 1] + " " + trunkNormals[i + 2]);
                    } else {
                        output.push("vn " + -trunkNormals[i] + " " + -trunkNormals[i + 1] + " " + -trunkNormals[i + 2]);
                    }
                }
            }
            if (trunkUV != null) {
                for (i = 0; i < trunkUV.length; i += 2) {
                    output.push("vt " + trunkUV[i] + " " + -trunkUV[i + 1]);
                }
            }
            for (i = 0; i < trunkFaces.length; i += 3) {
                var indices = [String(trunkFaces[i + 2] + v), String(trunkFaces[i + 1] + v), String(trunkFaces[i] + v)];
                var blanks = ["", "", ""];
                var facePositions = indices;
                var faceUVs = trunkUV != null ? indices : blanks;
                var faceNormals = trunkNormals != null ? indices : blanks;
                output.push("f " + facePositions[0] + "/" + faceUVs[0] + "/" + faceNormals[0] +
                    " " + facePositions[1] + "/" + faceUVs[1] + "/" + faceNormals[1] +
                    " " + facePositions[2] + "/" + faceUVs[2] + "/" + faceNormals[2]);
            }
            //back de previous matrix, to not change the original mesh in the scene
            if (globalposition && lastMatrix) {
                mesh[j].bakeTransformIntoVertices(lastMatrix);
            }
            v += curV;
        }
        var text = output.join("\n");
        return (text);
    }

    _ease(kind: EaseKind, t: number): number {
        switch (kind) {
            case "quadInOut":
                return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
            case "cubicInOut":
                return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
            case "quintOut": {            // 1 - (1 - t)^5
                const u = 1 - t;
                return 1 - u * u * u * u * u;
            }
            case "expoOut":               // t==1 ? 1 : 1 - 2^(-10 t)
                return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
            default:
                return t; // linear
        }
    }

    private _vecLerp(a: BABYLON.Vector3, b: BABYLON.Vector3, t: number): BABYLON.Vector3 {
        return new BABYLON.Vector3(
            a.x + (b.x - a.x) * t,
            a.y + (b.y - a.y) * t,
            a.z + (b.z - a.z) * t
        );
    }

    public cancelCameraAnimation(cam: CamLike, scene: BABYLON.Scene) {
        const active = this._activeAnim.get(cam);
        if (!active) return;
        scene.onBeforeRenderObservable.remove(active.obs);
        this._activeAnim.delete(cam);
        this._cameraAnimationActive = false;
        this._cameraAnimationFrameCounter = 0;
    }

    private _wrapPi(a: number): number {
        const twoPi = Math.PI * 2;
        a = ((a + Math.PI) % twoPi + twoPi) % twoPi - Math.PI;
        return a;
    }

    private async orbitToAngles(
        cam: CamLike,
        opts: {
            alpha?: number;                 // desired alpha (radians), optional
            beta?: number;                 // desired beta  (radians), optional
            durationMs?: number;            // default 600
            easing?: EaseKind;              // default "cubicInOut"
            clampBeta?: boolean;            // default true (avoid poles)
            betaEps?: number;               // default 1e-3
        } = {}
    ) {
        const {
            alpha,
            beta,
            durationMs = 600,
            easing = "cubicInOut",
            clampBeta = true,
            betaEps = 1e-3,
        } = opts;

        const c: any = cam;

        // Target & radius
        const target: BABYLON.Vector3 =
            (c.target?.clone?.() ?? c.target) ||
            (c.lockedTarget?.clone?.() ?? c.lockedTarget) ||
            c.position.add(c.getForwardRay().direction);

        const radius = typeof c.radius === "number"
            ? Math.abs(c.radius)
            : c.position.subtract(target).length();

        // Current angles: use camera's if present; otherwise derive from position-target
        const v = c.position.subtract(target);
        const r = v.length() || 1;
        const alpha0 = typeof c.alpha === "number" ? c.alpha : Math.atan2(v.z, v.x);
        const beta0 = typeof c.beta === "number" ? c.beta : Math.acos(Math.max(-1, Math.min(1, v.y / r)));

        // Desired angles
        let alpha1 = alpha != null ? alpha : alpha0;
        let beta1 = beta != null ? beta : beta0;

        // Pole safety & limits
        if (clampBeta) {
            beta1 = Math.min(Math.PI - betaEps, Math.max(betaEps, beta1));
        }

        // Shortest-path for alpha
        const a0 = this._wrapPi(alpha0);
        const a1 = this._wrapPi(alpha1);
        let dAlpha = this._wrapPi(a1 - a0);

        const b0 = beta0;
        const dBeta = beta1 - b0;

        // Freeze inertia for a deterministic tween
        const saved = {
            inertia: c.inertia ?? 0,
            panningInertia: c.panningInertia ?? 0,
            inertialRadiusOffset: c.inertialRadiusOffset ?? 0,
        };
        c.inertia = 0; c.panningInertia = 0; c.inertialRadiusOffset = 0;

        const t0 = performance.now();
        const token = Math.random();
        (this as any)._activeAnim ??= new WeakMap();
        (this as any)._activeAnim.set(cam, { token });

        await new Promise<void>((resolve) => {
            const tick = () => {
                const active = (this as any)._activeAnim.get(cam);
                if (!active || active.token !== token) return;

                const raw = Math.min(1, (performance.now() - t0) / durationMs);
                const k = this._ease(easing, raw);

                const a = a0 + dAlpha * k;
                const b = b0 + dBeta * k;

                // Recompute position from (alpha, beta, radius)
                const sinB = Math.sin(b), cosB = Math.cos(b);
                const x = radius * sinB * Math.cos(a);
                const y = radius * cosB;
                const z = radius * sinB * Math.sin(a);
                const pos = target.add(new BABYLON.Vector3(x, y, z));

                // Apply: target first, then position
                this._setTarget(cam, target);
                this._setPosition(cam, pos);

                this._sceneDirty = true;
                this._babScene.render();

                if (raw < 1) requestAnimationFrame(tick);
                else {
                    (this as any)._activeAnim.delete(cam);
                    // write back final alpha/beta if the cam supports them
                    if (typeof c.alpha === "number") c.alpha = a1;
                    if (typeof c.beta === "number") c.beta = beta1;

                    // restore inertia
                    c.inertia = saved.inertia;
                    c.panningInertia = saved.panningInertia;
                    c.inertialRadiusOffset = saved.inertialRadiusOffset;

                    resolve();
                }
            };
            requestAnimationFrame(tick);
        });
    }

    public setCameraInstant(
        scene: BABYLON.Scene,
        cam: CamLike,
        opts: {
            position?: BABYLON.Vector3;
            target?: BABYLON.Vector3;
            preserveForward?: boolean | "auto";
            lockForward?: { dir: BABYLON.Vector3; dist0: number; dist1: number };
        } = {}
    ): void {
        const {
            position,
            target,
            preserveForward = "auto",
            lockForward,
        } = opts;

        if (!position && !target) return;

        const c: any = cam;

        const startPos = c.position.clone();
        const currentTarget =
            c.target instanceof BABYLON.Vector3
                ? c.target.clone()
                : c.lockedTarget instanceof BABYLON.Vector3
                    ? c.lockedTarget.clone()
                    : startPos.add(c.getForwardRay().direction);

        const endPos = position ? position.clone() : startPos.clone();
        const endTgt = target ? target.clone() : currentTarget.clone();

        // Same forward-lock logic as animateCameraTo -------------------------------
        let useForwardLock = false;
        let fwdBasis: BABYLON.Vector3 | null = null;
        let dist1 = 0;

        if (lockForward) {
            useForwardLock = true;
            fwdBasis = lockForward.dir.clone().normalize();
            dist1 = lockForward.dist1;
        } else {
            const fwdInitial = currentTarget.subtract(startPos);
            const fwdFinal = endTgt.subtract(endPos);

            const canCompare =
                fwdInitial.lengthSquared() > 1e-12 &&
                fwdFinal.lengthSquared() > 1e-12;

            const cosTheta = canCompare
                ? BABYLON.Vector3.Dot(
                    fwdInitial.normalize(),
                    fwdFinal.normalize()
                )
                : 1;

            const COS_3_DEG = Math.cos(3 * Math.PI / 180);

            useForwardLock =
                preserveForward === true ||
                (preserveForward !== false && cosTheta >= COS_3_DEG);

            if (useForwardLock) {
                fwdBasis = fwdInitial.normalize();
                dist1 = fwdFinal.length();
            }
        }

        // Apply orientation ---------------------------------------------------------
        if (useForwardLock && fwdBasis) {
            // Position is target - forward * desired distance
            const pos = endTgt.subtract(fwdBasis.scale(dist1));
            this._setTarget(cam, endTgt);
            this._setPosition(cam, pos);
        } else {
            // Free placement
            this._setTarget(cam, endTgt);
            this._setPosition(cam, endPos);
        }

        // Render once
        this._sceneDirty = true;
        this._babScene.render();
    }

    public animateCameraTo(
        scene: BABYLON.Scene,
        cam: CamLike,
        opts: {
            position?: BABYLON.Vector3;
            target?: BABYLON.Vector3;
            maxMs?: number;
            easing?: EaseKind;
            onUpdate?: (pos: BABYLON.Vector3, tgt: BABYLON.Vector3) => void;
            targetLead?: number;
            preserveForward?: boolean | "auto";
            lockForward?: { dir: BABYLON.Vector3; dist0: number; dist1: number };
            kind?: CameraAnimKind;
            queueIfBlocked?: boolean;
        } = {}
    ): Promise<void> {
        const {
            position,
            target,
            maxMs = 2000,
            easing = "cubicInOut",
            onUpdate,
            targetLead = 0.2,
            preserveForward = "auto",
            lockForward,
            kind = "normal",
            queueIfBlocked = true,
        } = opts;

        const isInitial = kind === "initial";

        // Until initial animation completes, all non-initial requests must queue.
        if (!this._initialCameraAnimationCompleted && !isInitial) {
            if (!queueIfBlocked) {
                return Promise.resolve();
            }

            return new Promise<void>((resolve, reject) => {
                // Keep only the latest queued request
                if (this._queuedCameraRequest) {
                    this._queuedCameraRequest.resolve();
                }

                this._queuedCameraRequest = {
                    scene,
                    cam,
                    opts,
                    resolve,
                    reject
                };
            });
        }

        // Ignore duplicate initial requests while one is already running
        if (isInitial && this._initialCameraAnimationInProgress) {
            return Promise.resolve();
        }

        if (isInitial) {
            this._initialCameraAnimationInProgress = true;
        }

        if (!position && !target) {
            if (isInitial) {
                this._initialCameraAnimationInProgress = false;
                this._initialCameraAnimationCompleted = true;
            }
            return Promise.resolve();
        }

        this.cancelCameraAnimation(cam, scene);

        const applyPos = position !== undefined;
        const applyTgt = target !== undefined;

        const c: any = cam;
        const startPos = c.position.clone();

        const currentTarget =
            typeof c.getTarget === "function"
                ? (c.getTarget() as BABYLON.Vector3).clone()
                : c.target instanceof BABYLON.Vector3
                    ? c.target.clone()
                    : c.lockedTarget instanceof BABYLON.Vector3
                        ? c.lockedTarget.clone()
                        : startPos.add(c.getForwardRay().direction);

        const endPos = position ? position.clone() : startPos.clone();
        const endTgt = target ? target.clone() : currentTarget.clone();

        const posDelta = endPos.subtract(startPos).length();
        const tgtDelta = endTgt.subtract(currentTarget).length();

        const runQueuedIfAny = () => {
            if (!isInitial) return;

            this._initialCameraAnimationInProgress = false;
            this._initialCameraAnimationCompleted = true;

            const queued = this._queuedCameraRequest;
            this._queuedCameraRequest = null;

            if (queued) {
                try {
                    this.animateCameraTo(queued.scene, queued.cam, {
                        ...queued.opts,
                        kind: "normal"
                    }).then(queued.resolve, queued.reject);
                } catch (err) {
                    queued.reject(err);
                }
            }
        };

        if (posDelta < 1e-6 && tgtDelta < 1e-6) {
            if (applyTgt) this._setTarget(cam, endTgt);
            if (applyPos) this._setPosition(cam, endPos);
            this._sceneDirty = true;
            runQueuedIfAny();
            return Promise.resolve();
        }

        let useForwardLock = false;
        let fwdBasis: BABYLON.Vector3 | null = null;
        let dist0 = 0;
        let dist1 = 0;

        if (lockForward) {
            useForwardLock = true;
            fwdBasis = lockForward.dir.clone().normalize();
            dist0 = lockForward.dist0;
            dist1 = lockForward.dist1;
        } else {
            const fwdInitial = currentTarget.subtract(startPos);
            const fwdFinal = endTgt.subtract(endPos);

            const canCompare =
                fwdInitial.lengthSquared() > 1e-12 &&
                fwdFinal.lengthSquared() > 1e-12;

            const cosTheta = canCompare
                ? BABYLON.Vector3.Dot(fwdInitial.normalize(), fwdFinal.normalize())
                : 1;

            const COS_3_DEG = Math.cos(3 * Math.PI / 180);

            useForwardLock =
                preserveForward === true ||
                (preserveForward !== false && cosTheta >= COS_3_DEG);

            if (useForwardLock) {
                fwdBasis = fwdInitial.normalize();
                dist0 = fwdInitial.length();
                dist1 = fwdFinal.length();
            }
        }

        const savedInertia = c.inertia ?? 0;
        const savedPanningInertia = c.panningInertia ?? 0;
        const savedInertialRadiusOffset = c.inertialRadiusOffset ?? 0;

        c.inertia = 0;
        c.panningInertia = 0;
        c.inertialRadiusOffset = 0;

        const savedUniversal = {
            inertialMoveForward: c.inertialMoveForward ?? 0,
            inertialMoveRight: c.inertialMoveRight ?? 0,
            inertialMoveUp: c.inertialMoveUp ?? 0,
            inertialAlphaOffset: c.inertialAlphaOffset ?? 0,
            inertialBetaOffset: c.inertialBetaOffset ?? 0,
        };

        if (c.inertialMoveForward !== undefined) c.inertialMoveForward = 0;
        if (c.inertialMoveRight !== undefined) c.inertialMoveRight = 0;
        if (c.inertialMoveUp !== undefined) c.inertialMoveUp = 0;
        if (c.inertialAlphaOffset !== undefined) c.inertialAlphaOffset = 0;
        if (c.inertialBetaOffset !== undefined) c.inertialBetaOffset = 0;

        const hasOrbitAngles =
            typeof c.alpha === "number" &&
            typeof c.beta === "number";

        const alpha0 = hasOrbitAngles ? c.alpha : undefined;
        const beta0 = hasOrbitAngles ? c.beta : undefined;

        const distanceHint = Math.max(posDelta, tgtDelta);
        const minMs = 120;
        const ms = Math.max(
            minMs,
            Math.min(maxMs, maxMs * (distanceHint / (distanceHint + 1)))
        );

        const t0 = performance.now();
        const token = Math.random();

        let finished = false;

        return new Promise<void>((resolve) => {
            const finalize = () => {
                if (finished) return;
                finished = true;

                this._activeAnim.delete(cam);
                this._cameraAnimationActive = false;
                this._cameraAnimationFrameCounter = 0;

                c.inertia = savedInertia;
                c.panningInertia = savedPanningInertia;
                c.inertialRadiusOffset = savedInertialRadiusOffset;

                if (c.inertialMoveForward !== undefined) c.inertialMoveForward = savedUniversal.inertialMoveForward;
                if (c.inertialMoveRight !== undefined) c.inertialMoveRight = savedUniversal.inertialMoveRight;
                if (c.inertialMoveUp !== undefined) c.inertialMoveUp = savedUniversal.inertialMoveUp;
                if (c.inertialAlphaOffset !== undefined) c.inertialAlphaOffset = savedUniversal.inertialAlphaOffset;
                if (c.inertialBetaOffset !== undefined) c.inertialBetaOffset = savedUniversal.inertialBetaOffset;

                runQueuedIfAny();
            };

            this._activeAnim.set(cam, { obs: null as any, token });
            this._cameraAnimationActive = true;
            this._cameraAnimationFrameCounter = 0;

            const lead = Math.max(0, Math.min(0.9, targetLead));

            const tick = () => {
                const active = this._activeAnim.get(cam);

                if (!active || active.token !== token) {
                    finalize();
                    resolve();
                    return;
                }

                const raw = Math.min(1, (performance.now() - t0) / ms);
                const kT = this._ease(easing, raw);
                const tgt = this._vecLerp(currentTarget, endTgt, kT);

                let pos: BABYLON.Vector3;
                let lockedDistance = 0;

                if (useForwardLock && fwdBasis) {
                    const kLen =
                        raw > lead
                            ? this._ease(easing, (raw - lead) / (1 - lead))
                            : 0;

                    lockedDistance = dist0 + (dist1 - dist0) * kLen;
                    pos = tgt.subtract(fwdBasis.scale(lockedDistance));

                    if (hasOrbitAngles) {
                        c.alpha = alpha0;
                        c.beta = beta0;
                    }
                } else {
                    const kP =
                        raw > lead
                            ? this._ease(easing, (raw - lead) / (1 - lead))
                            : 0;

                    pos = this._vecLerp(startPos, endPos, kP);
                }

                if (isUniversalCamera(cam as any)) {
                    if (applyPos) this._setPosition(cam, pos);
                    if (applyTgt) this._setTarget(cam, tgt);
                } else {
                    if (applyTgt) this._setTarget(cam, tgt);
                    if (applyPos) this._setPosition(cam, pos);
                }

                c.inertialRadiusOffset = 0;

                if (onUpdate) {
                    onUpdate(pos, tgt);
                }

                this._sceneDirty = true;

                if (raw < 1) {
                    requestAnimationFrame(tick);
                } else {
                    finalize();
                    resolve();
                }
            };

            requestAnimationFrame(tick);
        });
    }

    private getCamTarget(cam: AnyCam): BABYLON.Vector3 {
        const c: any = cam;

        if (typeof c.getTarget === "function") return c.getTarget();
        if (c.target instanceof BABYLON.Vector3) return c.target.clone();
        if (c.lockedTarget instanceof BABYLON.Vector3) return c.lockedTarget.clone();

        const pos = cam.position.clone();
        const fwd = cam.getForwardRay().direction;
        return pos.add(fwd);
    }

    private setCamTarget(cam: AnyCam, v: BABYLON.Vector3) {
        const c: any = cam;

        if ("_currentTarget" in c) {
            if (c._currentTarget?.copyFrom) c._currentTarget.copyFrom(v);
            else c._currentTarget = v.clone();
            if (c.target?.copyFrom) c.target.copyFrom(v);
            else c.target = v.clone();
            return;
        }

        if (typeof c.setTarget === "function") { c.setTarget(v); return; }
        if ("lockedTarget" in c) { c.lockedTarget = v.clone(); return; }
        if ("target" in c) { c.target = v.clone(); return; }
    }

    private setCamPosition(cam: AnyCam, v: BABYLON.Vector3) {
        const c: any = cam;
        if (typeof c.setPosition === "function") c.setPosition(v);
        else cam.position.copyFrom(v);
    }

    private _setTarget(cam: BABYLON.Camera, v: BABYLON.Vector3) {
        if (isExamineCamera(cam as AnyCam)) {
            const ex: any = cam;

            if (ex._currentTarget?.copyFrom) ex._currentTarget.copyFrom(v);
            else ex._currentTarget = v.clone();

            if (ex.target?.copyFrom) ex.target.copyFrom(v);
            else ex.target = v.clone();

            return;
        }

        if (isUniversalCamera(cam as AnyCam)) {
            const u: any = cam;

            if (typeof u.setTarget === "function") {
                u.setTarget(v);
                return;
            }

            if ("lockedTarget" in u) u.lockedTarget = v.clone();
            else u.target = v.clone();

            return;
        }

        const c: any = cam;
        if (typeof c.setTarget === "function") { c.setTarget(v); return; }
        if ("lockedTarget" in c) { c.lockedTarget = v.clone(); return; }
        if ("target" in c) { c.target = v.clone(); return; }
    }

    private _createCrosshair(alpha: number = 0.75) {

        const ui = BABYLONGUI.AdvancedDynamicTexture.CreateFullscreenUI("UI");

        // Root container
        const container = new BABYLONGUI.Rectangle();
        container.width = "40px";
        container.height = "40px";
        container.thickness = 0;
        container.horizontalAlignment = BABYLONGUI.Control.HORIZONTAL_ALIGNMENT_CENTER;
        container.verticalAlignment = BABYLONGUI.Control.VERTICAL_ALIGNMENT_CENTER;

        ui.addControl(container);

        const black = "black";
        const white = "white";

        // Helpers
        const rect = (wPx: number, hPx: number, color: string) => {
            const r = new BABYLONGUI.Rectangle();
            r.width = `${wPx}px`;
            r.height = `${hPx}px`;
            r.background = color;
            r.thickness = 0;
            r.alpha = alpha;
            return r;
        };

        const filledCircle = (sizePx: number, color: string) => {
            const e = new BABYLONGUI.Ellipse();
            e.width = `${sizePx}px`;
            e.height = `${sizePx}px`;
            e.background = color;
            e.thickness = 0;
            e.alpha = alpha;
            return e;
        };

        const ring = (sizePx: number, thicknessPx: number, color: string) => {
            const e = new BABYLONGUI.Ellipse();
            e.width = `${sizePx}px`;
            e.height = `${sizePx}px`;
            e.color = color;
            e.thickness = thicknessPx;
            e.alpha = alpha;
            return e;
        };

        // === RINGS (high contrast on any background) ===
        // Outer white "halo"
        const outerWhite = ring(24, 2, white);
        outerWhite.zIndex = 0;
        container.addControl(outerWhite);

        // Outer black stroke (gives contrast on light backgrounds)
        const outerBlack = ring(22, 2, black);
        outerBlack.zIndex = 1;
        container.addControl(outerBlack);

        // Inner white accent (helps on dark backgrounds)
        const innerWhite = ring(18, 2, white);
        innerWhite.zIndex = 2;
        container.addControl(innerWhite);

        // === CENTER ===
        const centerBlack = filledCircle(12, black);
        centerBlack.zIndex = 3;
        container.addControl(centerBlack);

        const centerDotWhite = filledCircle(4, white);
        centerDotWhite.zIndex = 4;
        container.addControl(centerDotWhite);

        // === ARMS (dual-tone: black outline + white core) ===
        const armLength = 12;       // even
        const armCore = 2;          // even
        const armOutline = 4;       // even
        const armOffset = 10;       // even

        const createDualToneArm = (horizontal: boolean, offset: number) => {

            // Black outline (slightly thicker)
            const outline = rect(
                horizontal ? armLength : armOutline,
                horizontal ? armOutline : armLength,
                black
            );

            // White core (centered inside)
            const core = rect(
                horizontal ? armLength : armCore,
                horizontal ? armCore : armLength,
                white
            );

            if (horizontal) {
                outline.left = `${offset}px`;
                core.left = `${offset}px`;
            } else {
                outline.top = `${offset}px`;
                core.top = `${offset}px`;
            }

            outline.zIndex = 5;
            core.zIndex = 6;

            container.addControl(outline);
            container.addControl(core);
        };

        // vertical (top/bottom)
        createDualToneArm(false, -armOffset);
        createDualToneArm(false, armOffset);

        // horizontal (left/right)
        createDualToneArm(true, -armOffset);
        createDualToneArm(true, armOffset);

        this._crosshair = container;
    }

    private _setPosition(cam: BABYLON.Camera, v: BABYLON.Vector3) {
        if (isExamineCamera(cam as AnyCam)) {
            const ex: any = cam;

            if (typeof ex.setPosition === "function") {
                ex.setPosition(v);
            } else if (ex.position?.copyFrom) {
                ex.position.copyFrom(v);
            } else {
                ex.position = v.clone();
            }
            return;
        }

        if (isUniversalCamera(cam as AnyCam)) {
            const u: any = cam;

            if (u.position?.copyFrom) u.position.copyFrom(v);
            else u.position = v.clone();

            return;
        }

        const c: any = cam;
        if (typeof c.setPosition === "function") { c.setPosition(v); return; }
        if (c.position?.copyFrom) c.position.copyFrom(v);
        else c.position = v.clone();
    }


    getObjMaterialString(mesh: BABYLON.Mesh) {
        var output = [];
        var m = mesh.material as any;
        output.push("newmtl " + mesh.material.id);
        output.push("  Ns " + m.specularPower.toFixed(4));
        output.push("  Ni 1.5000");
        output.push("  d " + m.alpha.toFixed(4));
        output.push("  Tr 0.0000");
        output.push("  Tf 1.0000 1.0000 1.0000");
        output.push("  illum 2");
        output.push("  Ka " + m.ambientColor.r.toFixed(4) + " " + m.ambientColor.g.toFixed(4) + " " + m.ambientColor.b.toFixed(4));
        output.push("  Kd " + m.diffuseColor.r.toFixed(4) + " " + m.diffuseColor.g.toFixed(4) + " " + m.diffuseColor.b.toFixed(4));
        output.push("  Ks " + m.specularColor.r.toFixed(4) + " " + m.specularColor.g.toFixed(4) + " " + m.specularColor.b.toFixed(4));
        output.push("  Ke " + m.emissiveColor.r.toFixed(4) + " " + m.emissiveColor.g.toFixed(4) + " " + m.emissiveColor.b.toFixed(4));

        var uvscale = "";
        if (m.ambientTexture) {
            output.push("  map_Ka " + uvscale + m.ambientTexture.name);
        }
        if (m.diffuseTexture) {
            output.push("  map_Kd " + uvscale + m.diffuseTexture.name);
        }
        if (m.specularTexture) {
            output.push("  map_Ks " + uvscale + m.specularTexture.name);
        }

        if (m.bumpTexture) {
            output.push("  map_bump -imfchan z " + uvscale + m.bumpTexture.name);
        }
        if (m.opacityTexture) {
            output.push("  map_d " + uvscale + m.opacityTexture.name);
        }
        var text = output.join("\n");
        return (text);
    }


    exportSceneToOBJ() {


        var that = this;
        let options = {
            shouldExportNode: function (node: BABYLON.TransformNode) {
                return node !== that._skyBox;
            },
        };

        var opt: BABYLONEXPORT.IExportOptions = {};

        opt.shouldExportNode =
            function (node: BABYLON.TransformNode) {
                return (node.name != "skyBox" && node.isEnabled());
            };

        var matString = "";
        var matIds: string[] = [];
        var meshes: BABYLON.Mesh[] = [];

        for (var x = 0; x < this.scene.meshes.length; x++) {

            if (this.scene.meshes[x].material != null && this.scene.meshes[x] != this._skyBox && this.scene.meshes[x].isEnabled()) {
                matIds.push(this.scene.meshes[x].material.id);
                meshes.push(this.scene.meshes[x] as BABYLON.Mesh);
                matString += this.getObjMaterialString(this.scene.meshes[x] as BABYLON.Mesh) + "\n";
            }
        }

        var objString = this.generateObjExportString(meshes, true, "mat", true);

        var mtllink = BABYLON.Tools.FileAsURL(matString);
        var objlink = BABYLON.Tools.FileAsURL(objString);

        var link = window.document.createElement("a");

        link.href = objlink;
        link.download = "OBJScene.obj";
        var click = document.createEvent("MouseEvents");
        click.initEvent("click", true, false);
        link.dispatchEvent(click);

        link.href = mtllink;
        link.download = "mat.mtl";
        var click = document.createEvent("MouseEvents");
        click.initEvent("click", true, false);
        link.dispatchEvent(click);
    }

    exportSceneToGLB() {

        var that = this;
        let options = {
            shouldExportNode: function (node: BABYLON.TransformNode) {
                return node !== that._skyBox;
            },
        };

        //for (var x = 0; x < that._babScene.meshes.length; x = x + 50) {
        var opt: BABYLONEXPORT.IExportOptions = {};

        opt.shouldExportNode =
            function (node: BABYLON.TransformNode) {
                return (node.name != "skyBox" && node.isEnabled());
            };

        BABYLONEXPORT.GLTF2Export.GLBAsync(this.scene as any, "GLB Scene", opt).then((glb) => {
            glb.downloadFiles();
        });
        //}
    }


    exportSceneToGLTF() {

        var that = this;
        let options = {
            shouldExportNode: function (node: BABYLON.TransformNode) {
                return node !== that._skyBox;
            },
        };

        var opt: BABYLONEXPORT.IExportOptions = {};

        opt.shouldExportNode =
            function (node: BABYLON.TransformNode) {
                return (node.name != "skyBox" && node.isEnabled());
            };

        BABYLONEXPORT.GLTF2Export.GLTFAsync(this.scene as any, "GLTF Scene", opt).then((glb) => {
            glb.downloadFiles();
        });

    }

    clampSceneToBoundingBox(bbox: BABYLON.BoundingBox) {

        var scene = this._babScene;
        var min = bbox.minimumWorld;
        var max = bbox.maximumWorld;

        // No padding. Note that octant bounds do not consider some object's rotation, meaning we can have smaller clip planes than the actual content.
        // Because of this, clip planes based on content might be too small!
        var eps = 0.0; 

        // X >= min.x  -> clip x < min.x
        scene.clipPlane = new BABYLON.Plane(-1, 0, 0, min.x - eps);

        // X <= max.x  -> clip x > max.x
        scene.clipPlane2 = new BABYLON.Plane(1, 0, 0, -max.x - eps);

        // Z >= min.z  -> clip z < min.z
        scene.clipPlane3 = new BABYLON.Plane(0, 0, -1, min.z - eps);

        // Z <= max.z  -> clip z > max.z
        scene.clipPlane4 = new BABYLON.Plane(0, 0, 1, -max.z - eps);

        scene.clipPlane5 = null;
        scene.clipPlane6 = null;

    }

    public async setCamera(cameraPosition: BABYLON.Vector3, focusPosition: BABYLON.Vector3, skipAnimation: boolean) {
        const durationMs = skipAnimation ? 0 : 2000; // TODO!!!
        const cam = this.scene.activeCamera as unknown as CamLike;

        await this.animateCameraTo(this._babScene, cam, {
            target: focusPosition.clone(),
            position: cameraPosition,
            maxMs: durationMs,
            easing: "cubicInOut",
            targetLead: 0.2,
            preserveForward: false       // allow rotation to face the new target
        });
    }

    public async focusCamera(focusPosition: BABYLON.Vector3, durationMs: number = 2000) {
        const cam = this.scene.activeCamera as unknown as CamLike;
        const c: any = cam;

        // If focus == position, nothing to do
        if (BABYLON.Vector3.DistanceSquared(c.position, focusPosition) < 1e-12) return;

        // Keep camera position fixed; animate only the target
        const fixedPos = c.position.clone();

        await this.animateCameraTo(this._babScene, cam, {
            target: focusPosition.clone(),
            position: fixedPos,          // hold position constant
            maxMs: durationMs,
            easing: "cubicInOut",
            targetLead: 0.9,             // target effectively moves first; position is fixed anyway
            preserveForward: false       // allow rotation to face the new target
        });
    }
    public async alignCamera(vector: BABYLON.Vector3) {
        const cam = this.scene.activeCamera as any; // AnyCam/ CamLike-ish
        if (!cam) return;

        // Desired viewing direction (world space)
        const v = vector.clone().normalize();
        if (v.lengthSquared() < 1e-12) return;

        if (isExamineCamera(cam)) {
            const c: any = cam;

            const target: BABYLON.Vector3 =
                (c.target?.clone?.() ?? c.target) ||
                (c.lockedTarget?.clone?.() ?? c.lockedTarget) ||
                c.position.add(c.getForwardRay().direction);

            // Convert view dir -> (alpha,beta) in ArcRotate convention
            let alphaDesired = Math.atan2(v.z, v.x);
            let betaDesired = Math.acos(Math.max(-1, Math.min(1, v.y)));

            // Pole handling
            const POLE_EPS = 1e-6;
            const ALPHA_NORTH = -Math.PI / 2;
            if (Math.abs(v.y) > 1 - POLE_EPS) alphaDesired = ALPHA_NORTH;

            await this.orbitToAngles(cam, {
                alpha: alphaDesired,
                beta: betaDesired,
                durationMs: 600,
                easing: "cubicInOut",
                clampBeta: true,
                betaEps: 1e-3,
            });

            this._sceneDirty = true;
            return;
        }

        if (isUniversalCamera(cam)) {
            const u: any = cam;
            const pos = u.position.clone();

            const curTarget: BABYLON.Vector3 =
                (typeof u.getTarget === "function" ? u.getTarget() : u.target) ||
                pos.add(u.getForwardRay().direction);

            const dist = Math.max(0.001, BABYLON.Vector3.Distance(pos, curTarget));
            const dir = vector.clone().normalize().scale(-1);
            const endTarget = pos.add(dir.scale(dist));

            // Clear inertia so it doesn't fight the tween
            if (u.inertialAlphaOffset !== undefined) u.inertialAlphaOffset = 0;
            if (u.inertialBetaOffset !== undefined) u.inertialBetaOffset = 0;
            if (u.inertialMoveForward !== undefined) u.inertialMoveForward = 0;
            if (u.inertialMoveRight !== undefined) u.inertialMoveRight = 0;
            if (u.inertialMoveUp !== undefined) u.inertialMoveUp = 0;

            await this.animateCameraTo(this._babScene, u as any, {
                
                target: endTarget,
                maxMs: 2000,
                easing: "cubicInOut",
                targetLead: 1.0,        // target-only tween
                preserveForward: false,
            });

            this._sceneDirty = true;
            return;
        }

        
        if (typeof cam.setTarget === "function") {
            const pos: BABYLON.Vector3 = cam.position.clone();
            cam.setTarget(pos.add(v.scale(10)));
            this._sceneDirty = true;
        }
    }



    zoomToExtents(minimum: BABYLON.Vector3, maximum: BABYLON.Vector3) {

        if (this._followCamera != null && this._babScene.activeCamera == this._followCamera)
            return;

        var boundsToZoom: BABYLON.BoundingBox = new BABYLON.BoundingBox(minimum, maximum);
        this.viewBoundingBox(boundsToZoom);
    }

    viewObjects(objectIds: string[]) {

        if (objectIds == null)
            return;

        var boundsToZoom: BABYLON.BoundingBox;

        for (var i = 0; i < objectIds.length; i++) {

            var extents = this._meshManager.getExtentsOfObject(objectIds[i]);

            if (extents != null) {

                if (boundsToZoom == null) {
                    boundsToZoom = new BABYLON.BoundingBox(extents[0], extents[1]);
                }
                else {
                    boundsToZoom.minimum.x = Math.min(boundsToZoom.minimum.x, extents[0].x);
                    boundsToZoom.minimum.y = Math.min(boundsToZoom.minimum.y, extents[0].y);
                    boundsToZoom.minimum.z = Math.min(boundsToZoom.minimum.z, extents[0].z);

                    boundsToZoom.maximum.x = Math.max(boundsToZoom.maximum.x, extents[1].x);
                    boundsToZoom.maximum.y = Math.max(boundsToZoom.maximum.y, extents[1].y);
                    boundsToZoom.maximum.z = Math.max(boundsToZoom.maximum.z, extents[1].z);
                }
            }
        }

        this.viewBoundingBox(boundsToZoom);
    }

    public async viewBoundingBox(
        box: BABYLON.BoundingBox,
        aspect: number = 1.0,
        slack: number = 1.0, 
        opts?: {
            animate?: boolean;          // default: true
            durationMs?: number;        // default: 800
            easing?: EaseKind;          // default: "cubicInOut"
            targetLead?: number;        // default: 0.2
        }
    ) {

        if (!box || !box.minimum || !box.maximum) return;

        const cam = this.scene.activeCamera as unknown as CamLike;
        const c: any = cam;

        // Bounding sphere around the box
        const bSphere = new BABYLON.BoundingSphere(box.minimum, box.maximum);
        const zoomRadius = Math.max(bSphere.radius, 0.5);

        // Current forward
        const currTarget: BABYLON.Vector3 =
            (c.target?.clone?.() ?? c.target) ||
            (c.lockedTarget?.clone?.() ?? c.lockedTarget) ||
            c.position.add(c.getForwardRay().direction);
        const fwd = currTarget.subtract(c.position).normalize();

        // View parameters
        let hOverD = Math.tan(c.fov / 2.0);
        if (aspect < 1.0) hOverD *= aspect;

        // Distance from eye to center that fits the sphere in view
        const distToCenter = zoomRadius / hOverD;

        // Final pose (same forward; target = center; position = center - fwd * desiredDistance)
        const finalTarget = bSphere.center.clone();
        const isOrtho = c.mode === BABYLON.Camera.ORTHOGRAPHIC_CAMERA;

        const desiredRadius = isOrtho
            ? distToCenter / Math.max(c.radiusToOrthoScale || 1, 1e-6)
            : distToCenter;

        const finalPos = finalTarget.subtract(fwd.scale(Math.abs(desiredRadius)));

        // Animation options
        const animate = opts?.animate ?? true;
        const durationMs = opts?.durationMs ?? 2000;
        const easing = opts?.easing ?? "cubicInOut";
        const targetLead = opts?.targetLead ?? 0.2;

        if (animate) {
            await this.animateCameraTo(this._babScene, cam, {
                target: finalTarget,
                position: finalPos,
                maxMs: durationMs,
                easing,
                targetLead,
                preserveForward: false, // lock the forward heading; just slide along it
            });

            // For orthographic, ensure near/far & view matrices are consistent post-tween
            if (isOrtho) {
                c.minZ = -10000;
                (cam as any).getProjectionMatrix(true);
                (cam as any).updateCache(true);
                (cam as any).computeViewMatrix(true);
                this.setCameraMode(BABYLON.Camera.ORTHOGRAPHIC_CAMERA);
            } else {
                c.minZ = 0.5;
            }
        } else {

 

            // Instant path (original behavior, minus the inertial dolly)
            c._currentTarget = finalTarget;
            c.setPosition(finalPos);

            if (isOrtho) {
                c.minZ = -10000;
                (cam as any).getProjectionMatrix(true);
                (cam as any).updateCache(true);
                (cam as any).computeViewMatrix(true);
                this.setCameraMode(BABYLON.Camera.ORTHOGRAPHIC_CAMERA);
            } else {
                c.minZ = 0.5;
            }

            this._babScene.render();
            c.computeViewMatrix?.();
        }

        this._sceneDirty = true;
    }

    getScreenshot(width: number, height: number, callbackFn: (base64Image: string) => void) {
        const activeCamera = this.scene.activeCamera as AnyCam & {
            widthScale?: number;
            heightScale?: number;
            computeViewMatrix?: (force?: boolean) => BABYLON.Matrix;
            updateCache?: (force?: boolean) => void;
        };
        const renderCanvas = this._babEngine.getRenderingCanvas();
        const originalRenderWidth = this._babEngine.getRenderWidth();
        const originalRenderHeight = this._babEngine.getRenderHeight();
        const minimumCaptureWidth = 1280;
        const requestedAspectRatio = height > 0 ? width / height : 1;
        const captureWidth = Math.max(width, minimumCaptureWidth);
        const captureHeight = Math.max(
            height,
            Math.round(captureWidth / Math.max(requestedAspectRatio, 0.000001))
        );
        const originalCanvasStyleWidth = renderCanvas?.style.width;
        const originalCanvasStyleHeight = renderCanvas?.style.height;
        const originalWidthScale = activeCamera?.widthScale;
        const originalHeightScale = activeCamera?.heightScale;
        const originalOrthoTop = activeCamera?.orthoTop;
        const originalOrthoBottom = activeCamera?.orthoBottom;
        const originalOrthoLeft = activeCamera?.orthoLeft;
        const originalOrthoRight = activeCamera?.orthoRight;
        let restored = false;

        const refreshCameraMatrices = () => {
            activeCamera?.getProjectionMatrix?.(true);
            activeCamera?.updateCache?.(true);
            activeCamera?.computeViewMatrix?.(true);
        };

        const restoreRenderState = () => {
            if (restored) {
                return;
            }

            restored = true;

            if (renderCanvas) {
                renderCanvas.style.width = originalCanvasStyleWidth;
                renderCanvas.style.height = originalCanvasStyleHeight;
            }

            this._babEngine.setSize(originalRenderWidth, originalRenderHeight);

            if (activeCamera?.mode === BABYLON.Camera.ORTHOGRAPHIC_CAMERA) {
                activeCamera.orthoTop = originalOrthoTop;
                activeCamera.orthoBottom = originalOrthoBottom;
                activeCamera.orthoLeft = originalOrthoLeft;
                activeCamera.orthoRight = originalOrthoRight;

                if (originalWidthScale != null) {
                    activeCamera.widthScale = originalWidthScale;
                }
                if (originalHeightScale != null) {
                    activeCamera.heightScale = originalHeightScale;
                }
            }

            refreshCameraMatrices();
            this.scene.render();
        };

        if (renderCanvas) {
            renderCanvas.style.width = `${captureWidth}px`;
            renderCanvas.style.height = `${captureHeight}px`;
        }

        this._babEngine.setSize(captureWidth, captureHeight);

        if (activeCamera?.mode === BABYLON.Camera.ORTHOGRAPHIC_CAMERA) {
            const aspectRatio = captureHeight > 0 ? captureWidth / captureHeight : 1;
            const halfVerticalSpan = Math.max(
                Math.abs(activeCamera.orthoTop ?? 0),
                Math.abs(activeCamera.orthoBottom ?? 0),
                0.000001
            );

            activeCamera.orthoTop = halfVerticalSpan;
            activeCamera.orthoBottom = -halfVerticalSpan;
            activeCamera.orthoLeft = -halfVerticalSpan * aspectRatio;
            activeCamera.orthoRight = halfVerticalSpan * aspectRatio;

            if (activeCamera.widthScale != null) {
                activeCamera.widthScale = aspectRatio;
            }
            if (activeCamera.heightScale != null) {
                activeCamera.heightScale = aspectRatio !== 0 ? 1 / aspectRatio : 1;
            }
        }

        refreshCameraMatrices();
        this.scene.render();

        try {
            BABYLON.Tools.CreateScreenshot(this._babEngine, this.scene.activeCamera, {
                width: captureWidth, height: captureHeight
            }, function (base64Image) {
                restoreRenderState();

                if (captureWidth === width && captureHeight === height) {
                    callbackFn(base64Image);
                    return;
                }

                const image = new Image();
                image.onload = () => {
                    const thumbnailCanvas = document.createElement('canvas');
                    thumbnailCanvas.width = width;
                    thumbnailCanvas.height = height;

                    const context = thumbnailCanvas.getContext('2d');
                    if (!context) {
                        callbackFn(base64Image);
                        return;
                    }

                    context.imageSmoothingEnabled = true;
                    context.imageSmoothingQuality = 'high';
                    context.drawImage(image, 0, 0, captureWidth, captureHeight, 0, 0, width, height);
                    callbackFn(thumbnailCanvas.toDataURL('image/png'));
                };
                image.onerror = () => callbackFn(base64Image);
                image.src = base64Image;
            });
        }
        catch (e) {
            restoreRenderState();
            throw e;
        }
    }

    async getPanoramaImage(size: number, callbackFn: (base64Image: string) => void) {
        try {
            const capturePosition = this.scene.activeCamera?.globalPosition?.clone?.()
                ?? this.scene.activeCamera?.position?.clone?.()
                ?? BABYLON.Vector3.Zero();
            const pixelData = await BABYLON.captureEquirectangularFromScene(this.scene, {
                size,
                position: capturePosition
            });

            if (!pixelData) {
                callbackFn('');
                return;
            }

            const width = size * 2;
            const height = size;
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;

            const context = canvas.getContext('2d');
            if (!context) {
                callbackFn('');
                return;
            }

            const imageData = context.createImageData(width, height);

            if (pixelData instanceof Float32Array) {
                for (let index = 0; index < pixelData.length; index++) {
                    const channel = pixelData[index];
                    imageData.data[index] = Math.max(0, Math.min(255, Math.round(channel * 255)));
                }
            }
            else {
                const rgbaData = new Uint8ClampedArray(pixelData.buffer, pixelData.byteOffset, pixelData.byteLength);
                imageData.data.set(rgbaData);
            }

            context.putImageData(imageData, 0, 0);
            callbackFn(canvas.toDataURL('image/png'));
        }
        catch (error) {
            console.error('Failed to capture knowledge view panorama image.', error);
            callbackFn('');
        }
    }

    private doWork(allowedTime: number): boolean {

        var tStart = BABYLON.Tools.Now;
        var tCur = tStart;

        var numMeshData = 0;
        var avgMeshParseTime = 0;

        while ((tCur - tStart + avgMeshParseTime) <= allowedTime) {
            var numProcessedData = this._displayManager.processPendingDisplayData(1);
            if (numProcessedData > 0) {
                numMeshData += numProcessedData;
                var camera = this.scene.activeCamera as AnyCam;

                if (isExamineCamera(camera)) {
                    camera.updateZoomSpeed(this._meshManager.meshExtents);
                }
            }
            else {
                break;
            }

            tCur = BABYLON.Tools.Now;
            avgMeshParseTime = (tCur - tStart) / numProcessedData;
        }

        if (numProcessedData > 0) {
            //console.log("Processed " + numProcessedData + " mesh data from web workers in " + (tCur - tStart) + " ms. Allowed time was " + allowedTime + " ms. Avg mesh processing time is " + avgMeshParseTime + " ms.");
            return true;
        }
        else {
            return false;
        }
    }

    private _targetStillFrameTime = 500; // 2 FPS
    private _targetMovingFrameTime = 100; // 10 FPS
    private _framesCounter = 1;
    private _lastFrameTime = 0;

    private _sceneDirty: boolean = false;

    private _syncThinInstanceMotionMode(cameraMoving: boolean): void {
        const shouldUseInstanceOnlyMode = this.enableThinInstanceMotionMode && cameraMoving;

        if (this._thinInstanceMotionModeActive === shouldUseInstanceOnlyMode) {
            return;
        }

        this._thinInstanceMotionModeActive = shouldUseInstanceOnlyMode;
        this._meshManager.setSpsMeshesEnabled(!shouldUseInstanceOnlyMode);
        this._sceneDirty = true;
        this._meshManager.meshesDirty = true;
    }

    private hasNewSceneContent(): boolean {
        var camera = this.scene.activeCamera as any;

        var time = BABYLON.Tools.Now;

        var different = time - this._lastFrameTime;

        return this._sceneDirty || this._meshManager.meshesDirty || this._displayManager.hasPendingDisplayData() || different > this._targetStillFrameTime;
    }

    private forceUpdateLabels(): boolean {
        var time = BABYLON.Tools.Now;

        var different = time - this._lastFrameTime;

        return different > this._targetStillFrameTime;
    }

    private _tryParseAssemblyMeshId(meshId: any): [string, string, string] | null {
        if (Array.isArray(meshId)) {
            return meshId as [string, string, string];
        }

        if (typeof meshId !== 'string') {
            return null;
        }

        const trimmed = meshId.trim();

        if (!trimmed.startsWith('[')) {
            return null;
        }

        try {
            const parsed = JSON.parse(trimmed);

            if (
                Array.isArray(parsed) &&
                parsed.length === 3 &&
                typeof parsed[0] === 'string' &&
                typeof parsed[1] === 'string' &&
                typeof parsed[2] === 'string'
            ) {
                return parsed as [string, string, string];
            }
        } catch {
        }

        return null;
    }

    private renderLoop() {
        const activeCamera = this.scene.activeCamera as any;
        const cameraMoving = !!(activeCamera && activeCamera.isMoving && activeCamera.isMoving());

        this._syncThinInstanceMotionMode(cameraMoving);

        if (this.hasNewSceneContent() || cameraMoving || this.scene.activeCamera == this._followCamera) {

            this._updateEdgeProcessRenderList();

            var renderLoopStartTime = BABYLON.Tools.Now;

            this._babScene.render();
            this._sceneDirty = false;
            this._meshManager.meshesDirty = false;

            this.drawMarkedPoints();

            var postRenderTime = BABYLON.Tools.Now;
            var timeSinceLastRender = postRenderTime - ((this._lastFrameTime > 0) ? this._lastFrameTime : renderLoopStartTime);

            var targetFrameTime = this.getFrameTargetTime();
            var workAllowedTime = targetFrameTime - timeSinceLastRender;

            if (workAllowedTime > 0) {
                this.doWork(workAllowedTime);
            }
            else {
                this.doWork(1);
            }

            this._framesCounter++;
            this._lastFrameTime = postRenderTime;
        }

        this._labelManager.calculateOccludedLabelsTimeCapped(4);
    }

    private getFrameTargetTime(): number {
        var camera = this.scene.activeCamera as any;
        var targetTime = this._targetStillFrameTime;

        if (camera && camera.isMoving()) {
            targetTime = this._targetMovingFrameTime;
        }

        return targetTime;
    }
}

class StoredCameraView {
    position: BABYLON.Vector3;
    target: BABYLON.Vector3;
    matrix: BABYLON.Matrix;
    radius: number;

    constructor(position: BABYLON.Vector3, target: BABYLON.Vector3, matrix: BABYLON.Matrix, radius: number) {
        this.position = position;
        this.target = target;
        this.matrix = matrix;
        this.radius = radius;
    }
}

class PickingInfo {
    type: Enumerations.PickObjectType;
    info: any;
    constructor(info: any, type: Enumerations.PickObjectType) {

        this.info = info;
        this.type = type;
    }
}

export default SceneManager;
